/**
 * AI-Mobile 手机端 PWA 主入口。
 *
 * 视图：
 *   - 连接：服务器地址 + 用户 token（持久化到 localStorage）
 *   - 供应商：列表 / 切换 / 新增 / 删除
 *   - 监控：CPU / 内存 / 磁盘 / 电量 / Claude 会话数（探针推送 probe.data，分层采样）
 */
import { WsClient, normalizeWsUrl } from './ws_client';
import { h, clear, toast, input } from './ui';
import { renderCode } from './highlight';
import type {
  BridgeStatus,
  ChannelStatusResponse,
  CcguiStatusResponse,
  DeviceStatus,
  DisplayStatusResponse,
  FsEntry,
  FsListResponse,
  FsReadResponse,
  ProviderInfo,
  ProviderListResponse,
  ProviderModelsResponse,
  ProviderTargetState,
  ProbeData,
  ScreenStartResult,
  SysMonitorResponse,
  WindowInfo,
} from '@ai-mobile/shared';

/* ------------------------------ 状态 ------------------------------ */

interface AppState {
  serverUrl: string;
  token: string;
  devices: DeviceStatus[];
  selected: string;
  connected: boolean;
  authError: string;
  providers: ProviderInfo[];
  targets: ProviderTargetState[];
  status: BridgeStatus | null;
  tab: 'devices' | 'providers' | 'monitor' | 'live' | 'screen' | 'files' | 'settings';
  /** 系统监控数据（sys.monitor 缓存，监控 tab 用；探针模式下由 probe.data 帧合成） */
  monitor: SysMonitorResponse | null;
  /** sys.monitor 最近一次错误（空 = 无） */
  monitorErr: string;
  /** 探针慢层最近一次到达时间戳（0 = 未到达，监控 tab 对应行显示「采集中…」） */
  monitorSlowTs: number;
  /** 探针 ctrl 层（快捷控制三状态）；null = 本轮 watch 尚未收到 ctrl 帧（显示「查询中」） */
  ctrl: {
    display: DisplayStatusResponse | null;
    ccgui: CcguiStatusResponse | null;
    channel: ChannelStatusResponse | null;
  } | null;
  /** 应用窗口列表（app.list 缓存） */
  windows: WindowInfo[];
  /** 主界面头部面板折叠（持久化） */
  headerCollapsed: boolean;
  /** 文件浏览：当前目录（绝对路径，空 = 未初始化） */
  fsPath: string;
  /** 当前目录的上级（盘根为 null） */
  fsParent: string | null;
  /** 当前目录条目（fs.list 缓存） */
  fsEntries: FsEntry[];
  /** 正在查看的文件（null = 显示目录列表） */
  fsViewing: { path: string; text: string; truncated: boolean; size: number } | null;
  /** fs.list / fs.read 进行中 */
  fsLoading: boolean;
}

const LS = { url: 'aim.url', token: 'aim.token', device: 'aim.device', font: 'aim.font', hdr: 'aim.hdr', fsdir: 'aim.fsdir', fsfile: 'aim.fsfile', floatEnabled: 'aim.floatEnabled', floatDevice: 'aim.floatDevice', floatOpacity: 'aim.floatOpacity', floatRows: 'aim.floatRows' };
const load = (k: string): string => {
  try {
    return localStorage.getItem(k) ?? '';
  } catch {
    return '';
  }
};
const save = (k: string, v: string): void => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* noop */
  }
};

const client = new WsClient();

/** tab 键顺序（按钮与面板映射的单一来源，须与 AppState['tab'] 一致） */
const TAB_KEYS = ['devices', 'providers', 'monitor', 'live', 'screen', 'files', 'settings'] as const;

/* --------------------- 字体大小（全局缩放，持久化） --------------------- */

const FONT_LEVELS: [string, number][] = [
  ['小', 0.9],
  ['标准', 1],
  ['大', 1.1],
  ['特大', 1.25],
];
let fontScale = 1;
{
  const v = parseFloat(load(LS.font));
  if (FONT_LEVELS.some(([, s]) => s === v)) fontScale = v;
}
function applyFontScale(): void {
  document.documentElement.style.setProperty('--fscale', String(fontScale));
}
function stepFont(dir: -1 | 1): void {
  const idx = FONT_LEVELS.findIndex(([, s]) => s === fontScale);
  const next = Math.max(0, Math.min(FONT_LEVELS.length - 1, idx + dir));
  if (next === idx) {
    toast(dir > 0 ? '已是最大字号' : '已是最小字号');
    return;
  }
  fontScale = FONT_LEVELS[next][1];
  save(LS.font, String(fontScale));
  applyFontScale();
  toast(`字体：${FONT_LEVELS[next][0]}`, 'ok');
}
applyFontScale();
const state: AppState = {
  serverUrl: load(LS.url),
  token: load(LS.token),
  devices: [],
  selected: load(LS.device),
  connected: false,
  authError: '',
  providers: [],
  targets: [],
  status: null,
  tab: 'devices',
  monitor: null,
  monitorErr: '',
  monitorSlowTs: 0,
  ctrl: null,
  windows: [],
  headerCollapsed: load(LS.hdr) === '1',
  fsPath: '',
  fsParent: null,
  fsEntries: [],
  fsViewing: null,
  fsLoading: false,
};

const app = document.getElementById('app')!;
let connectStatusEl: HTMLElement | null = null;

/* 新版 Service Worker 接管（sw.js 的 skipWaiting）后自动刷新一次页面，
   避免旧外壳一直卡在缓存里（http 明文源没有 SW，addEventListener 会报错，需判空）。 */
let swReloaded = false;
navigator.serviceWorker?.addEventListener('controllerchange', () => {
  if (swReloaded) return;
  swReloaded = true;
  location.reload();
});

/* --------------------- 原生更新桥（非原生环境自动降级） --------------------- */

declare global {
  interface Window {
    AIMobileNative?: {
      getVersion: () => string;
      checkForUpdates: (cbId: string) => void;
      downloadAndInstall: (cbId: string, payload: string) => void;
      /** 原生清 WebView HTTP 缓存（旧版 APK 没有此方法，可选） */
      clearCache?: () => void;
      /* ---- 悬浮窗（v1.7.0+，旧 APK 无这些方法，全部可选） ---- */
      getFloatingState?: (cbId: string) => void;
      setFloatingConfig?: (cbId: string, payload: string) => void;
      setFloatingOpacity?: (v: number) => void;
      setFloatingPassThrough?: (cbId: string, enabled: boolean) => void;
      setFloatingRows?: (cbId: string, payload: string) => void;
      requestNotificationPermission?: (cbId: string) => void;
      openOverlaySettings?: () => void;
      openAppInfo?: () => void;
    };
    __aimUpdateResult?: (cbId: string, json: unknown) => void;
    __aimUpdateProgress?: (cbId: string, read: number, total: number) => void;
    __aimFloatResult?: (cbId: string, json: unknown) => void;
    __aimFloatEvent?: (json: unknown) => void;
  }
}
const native = () => window.AIMobileNative as Window['AIMobileNative'] | undefined;

// 模块级：当前挂起的更新流 id（同一时刻只允许一条）
let updateCbId: string | null = null;
let updateStatusEl: HTMLElement | null = null;
let updateBtnEl: HTMLButtonElement | null = null;

// 模块级：供应商页「配置哪个」下拉的状态（跨切换保持）
let cfgTarget = '';
let cfgProvider = '';
let cfgModel = '';
/** 供应商编辑表单：null=关闭，{ id: null }=新增，{ id }=编辑该供应商 */
let provForm: { id: string | null } | null = null;

// 已拉取的真实模型列表缓存（providerId → models），同一页面会话内切换供应商不重复请求
const providerModelsCache: Record<string, string[]> = {};

window.__aimUpdateResult = (cbId, json) => {
  if (cbId !== updateCbId) return;
  const r = json as { ok?: boolean; newer?: boolean; version?: string; versionCode?: number; sha256?: string; url?: string; name?: string; notes?: string[]; installed?: boolean; error?: string };
  if (!r.ok) {
    if (updateStatusEl) updateStatusEl.textContent = `🛈 ${r.error ?? '操作失败'}`;
    if (updateBtnEl) updateBtnEl.disabled = false;
    return;
  }
  // 一次完整下载安装结束（installed 字段出现）
  if (typeof r.installed === 'boolean') {
    if (updateStatusEl) updateStatusEl.textContent = r.installed ? '已拉起系统安装器，请按提示完成更新' : `🛈 ${r.error ?? '安装失败'}`;
    if (updateBtnEl) updateBtnEl.disabled = false;
    updateCbId = null;
    return;
  }
  // 检查结束：有新版则确认下载
  if (r.newer) {
    const cur = native()?.getVersion() ?? '?';
    const notes = (r.notes ?? []).slice(0, 5).map((n) => `· ${n}`).join('\n');
    const ok = confirm(`发现新版本 v${r.version}（当前 v${cur}）${r.name ? ' · ' + r.name : ''}\n\n${notes}\n\n是否下载并安装？`);
    if (!ok) {
      if (updateStatusEl) updateStatusEl.textContent = '已取消更新';
      if (updateBtnEl) updateBtnEl.disabled = false;
      updateCbId = null;
      return;
    }
    const downId = `d${Date.now()}`;
    updateCbId = downId;
    if (updateStatusEl) updateStatusEl.textContent = '下载中…';
    native()?.downloadAndInstall(downId, JSON.stringify({ versionCode: r.versionCode, url: r.url, sha256: r.sha256 }));
  } else {
    if (updateStatusEl) updateStatusEl.textContent = `已是最新版本 v${r.version}`;
    if (updateBtnEl) updateBtnEl.disabled = false;
    updateCbId = null;
  }
};

window.__aimUpdateProgress = (cbId, read, total) => {
  if (cbId !== updateCbId || !updateStatusEl) return;
  if (total > 0) {
    const pct = Math.floor((read / total) * 100);
    updateStatusEl.textContent = `下载中… ${pct}% · ${(read / 1048576).toFixed(1)}/${(total / 1048576).toFixed(1)}MB`;
  } else {
    updateStatusEl.textContent = `下载中… ${(read / 1048576).toFixed(1)}MB`;
  }
};

function checkForUpdateFromApp(): void {
  const n = native();
  if (updateBtnEl) updateBtnEl.disabled = true;
  if (updateStatusEl) updateStatusEl.textContent = '检查中…';
  if (!n) {
    if (updateStatusEl) updateStatusEl.textContent = '当前为浏览器环境，请打开 App 内的「检查更新」';
    if (updateBtnEl) updateBtnEl.disabled = false;
    return;
  }
  const cbId = `c${Date.now()}`;
  updateCbId = cbId;
  n.checkForUpdates(cbId);
}

/* --------------------- 悬浮窗（v1.7.0+，非原生环境自动降级） --------------------- */

type FloatResult = { ok: boolean } & Partial<{
  running: boolean; ready: boolean; deviceId: string;
  canDrawOverlays: boolean; notifGranted: boolean; isVivo: boolean; opacity: number;
  passThrough: boolean; rows: string;
  granted: boolean; code: string; error: string; enabled: boolean;
}>;

const pendingFloatCbs = new Map<string, (v: FloatResult) => void>();
let floatCbSeq = 0;
function floatCall(fn: (cbId: string) => void): Promise<FloatResult> {
  return new Promise((resolve) => {
    const cbId = `fl${++floatCbSeq}`;
    pendingFloatCbs.set(cbId, resolve);
    fn(cbId);
  });
}
window.__aimFloatResult = (cbId, json) => {
  const cb = pendingFloatCbs.get(cbId);
  if (cb) {
    pendingFloatCbs.delete(cbId);
    cb(json as FloatResult);
  }
};

// Service → PWA 事件流（仅 App 前台可达），原地刷新设置页状态行
let floatStatusEl: HTMLElement | null = null;
// 穿透开关勾选框（通知栏翻转时经 passChanged 事件同步）
let floatPassCb: HTMLInputElement | null = null;
function setFloatStatus(text: string, tone: 'ok' | 'err' | 'muted' = 'muted'): void {
  if (!floatStatusEl) return;
  floatStatusEl.textContent = text;
  floatStatusEl.className = tone === 'ok' ? 'ok small' : tone === 'err' ? 'err small' : 'muted small';
}
window.__aimFloatEvent = (json) => {
  const e = json as { event?: string; reason?: string };
  if (e.event === 'ready') setFloatStatus('运行中 · 已连接中继', 'ok');
  else if (e.event === 'started') setFloatStatus('悬浮窗已启动，正在连接…');
  else if (e.event === 'disconnected') setFloatStatus('连接断开，自动重试中…');
  else if (e.event === 'authFailed') setFloatStatus(`认证失败：${e.reason ?? ''}`, 'err');
  else if (e.event === 'error') setFloatStatus(`错误：${e.reason ?? ''}`, 'err');
  else if (e.event === 'stopped') setFloatStatus('悬浮窗已关闭');
  else if (e.event === 'passChanged') {
    if (floatPassCb) floatPassCb.checked = (e as { enabled?: boolean }).enabled === true;
  }
};

// 悬浮窗目标设备（默认跟随主界面选中设备）；透明度（0-100，0=全透明，与原生 FloatStore 钳制一致）
const floatDeviceId = (): string => load(LS.floatDevice) || state.selected;
const floatOpacityVal = (): number => {
  const v = parseInt(load(LS.floatOpacity), 10);
  return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 85;
};
// 面板可见内容行（与原生 FloatStore.FLOAT_ROWS_ALL 对应；空存储 = 全部）
const FLOAT_ROWS_ALL: [string, string][] = [
  ['cpu', 'CPU'],
  ['mem', '内存'],
  ['disk', '磁盘'],
  ['battery', '电量'],
  ['meta', 'CC 会话/运行'],
  ['channel', '通道'],
];
const floatRowsVal = (): string[] => {
  const v = load(LS.floatRows).trim();
  if (!v) return FLOAT_ROWS_ALL.map(([k]) => k);
  return v.split(',').map((s) => s.trim()).filter(Boolean);
};
function floatPayload(enabled: boolean): string {
  return JSON.stringify({
    enabled,
    url: state.serverUrl,
    token: state.token,
    deviceId: floatDeviceId(),
    opacity: floatOpacityVal(),
    rows: floatRowsVal().join(','),
  });
}

/** 清理缓存并重载：注销 Service Worker、清空 Cache Storage，原生壳里再清 WebView HTTP 缓存。
 * 返回被清理项的中文说明（用于提示文案）。 */
async function clearAppCache(): Promise<string> {
  const parts: string[] = [];
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
    if (regs.length) parts.push(`${regs.length} 个 Service Worker`);
  } catch {
    /* 忽略：部分环境无 SW */
  }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
      if (keys.length) parts.push(`${keys.length} 份离线缓存`);
    }
  } catch {
    /* 忽略 */
  }
  try {
    const n = native();
    if (n?.clearCache) {
      n.clearCache();
      parts.push('WebView 缓存');
    }
  } catch {
    /* 忽略：旧版 APK 无此方法 */
  }
  return parts.join(' + ');
}

/** 「检查更新」+「清理缓存」控件（连接页与主界面头部共用）：按钮行 + 状态行。 */
function updateControls(): { btn: HTMLElement; clrBtn: HTMLElement; status: HTMLElement } {
  const updBtn = h('button', { class: 'ghost small', text: '检查更新' });
  // 状态行初始显示当前版本；点检查更新后被进度/结果文案替换。空时靠 .upd-status:empty 不占位
  const updStatus = h('div', { class: 'upd-status muted small', style: 'margin-top:6px' });
  updStatus.textContent = native() ? `当前 v${native()!.getVersion()}` : '浏览器预览版';
  updateBtnEl = updBtn;
  updateStatusEl = updStatus;
  updBtn.onclick = checkForUpdateFromApp;

  const clrBtn = h('button', { class: 'ghost small', text: '清理缓存' });
  clrBtn.onclick = () => {
    if (!confirm('清理缓存并重新加载页面？\n用于页面卡在旧版本、按钮没更新等情况，不会影响已保存的配置。')) return;
    clrBtn.disabled = true;
    clrBtn.textContent = '清理中…';
    void clearAppCache().then((what) => {
      toast(what ? `已清理 ${what}，正在重新加载…` : '缓存已清理，正在重新加载…', 'ok');
      setTimeout(() => location.reload(), 800);
    });
  };
  return { btn: updBtn, clrBtn, status: updStatus };
}

/* ------------------------------ 工具 ------------------------------ */

function refreshProviders(): Promise<void> {
  return cmd('provider.list')
    .then((o) => {
      if (o.result.ok) {
        const data = o.result.data as ProviderListResponse | undefined;
        state.providers = data?.providers ?? [];
        state.targets = data?.targets ?? [];
        for (const k of Object.keys(providerModelsCache)) delete providerModelsCache[k];
      }
    })
    .catch((e) => toast(e.message));
}

function refreshStatus(): Promise<void> {
  return cmd('status')
    .then((o) => {
      if (o.result.ok) state.status = o.result.data as BridgeStatus;
    })
    .catch(() => {
      /* 非关键，忽略 */
    });
}

/** 下拉刷新：重新拉取供应商 + 状态，并整页重绘 */
function doRefresh(): Promise<void> {
  const tasks: Promise<unknown>[] = [refreshStatus()];
  if (state.selected) tasks.push(refreshProviders());
  return Promise.all(tasks)
    .then(() => {
      if (state.connected) render();
    })
    .catch((e) => toast((e as Error).message));
}

/* ------------------------------ 下拉刷新手势 ------------------------------ */

let prrEl: HTMLElement | null = null;
let prrSetup = false;

function setupPullToRefresh(): void {
  if (prrSetup) return;
  prrSetup = true;
  prrEl = h('div', { class: 'prr', text: '下拉刷新' });
  document.body.appendChild(prrEl);

  let startY = 0;
  let dy = 0;
  let active = false;

  app.addEventListener(
    'touchstart',
    (e) => {
      // 页面或触摸点所在的内部滚动容器（文件列表/面板）未滚到顶时，不触发下拉刷新
      const scroller = (e.target as HTMLElement | null)?.closest('.fs-scroll, .panel-wrap');
      const innerTop = !scroller || (scroller as HTMLElement).scrollTop <= 0;
      if (window.scrollY <= 0 && innerTop) {
        startY = e.touches[0].clientY;
        dy = 0;
        active = true;
      } else {
        active = false;
      }
    },
    { passive: true },
  );
  app.addEventListener(
    'touchmove',
    (e) => {
      if (!active || !prrEl) return;
      dy = e.touches[0].clientY - startY;
      if (dy <= 0) return;
      if (e.cancelable) e.preventDefault();
      const o = Math.min(dy, 90);
      prrEl.style.transform = `translateY(${o}px)`;
      prrEl.textContent = dy > 60 ? '松开刷新' : '下拉刷新';
    },
    { passive: false },
  );
  app.addEventListener('touchend', () => {
    if (!active || !prrEl) return;
    active = false;
    if (dy > 60) {
      prrEl.style.transform = 'translateY(0px)';
      prrEl.classList.add('ok');
      prrEl.textContent = '刷新中…';
      void doRefresh().finally(() => {
        if (!prrEl) return;
        prrEl.style.transform = 'translateY(-100%)';
        prrEl.textContent = '已刷新';
        setTimeout(() => {
          if (prrEl) {
            prrEl.textContent = '下拉刷新';
            prrEl.classList.remove('ok');
          }
        }, 700);
      });
      return;
    }
    prrEl.style.transform = 'translateY(-100%)';
    prrEl.textContent = '下拉刷新';
  });
}

async function cmd(action: string, params: Record<string, unknown> = {}, timeoutMs = 120_000) {
  if (!state.selected) throw new Error('请先选择设备');
  const out = await client.cmd(state.selected, action, params, timeoutMs);
  if (!out.result.ok) throw new Error(out.result.error ?? '命令失败');
  return out;
}

async function loadWindows(): Promise<void> {
  try {
    const o = await cmd('app.list', {}, 20_000);
    const data = o.result.data as { windows?: WindowInfo[] } | undefined;
    state.windows = data?.windows ?? [];
    // 前台窗口排最前，其余按进程名
    state.windows.sort((a, b) => (a.foreground === b.foreground) ? a.procName.localeCompare(b.procName) : (a.foreground ? -1 : 1));
    renderScreenAppList();
    renderLiveWinList();
  } catch (e) {
    toast((e as Error).message);
  }
}

let screenAppListBox: HTMLElement | null = null;

function renderScreenAppList(): void {
  const box = screenAppListBox;
  if (!box) return;
  clear(box);
  if (state.windows.length === 0) {
    box.append(h('div', { class: 'muted small' }, '没有可见窗口（点「刷新」重新枚举）'));
    return;
  }
  for (const w of state.windows.slice(0, 40)) {
    const row = h('div', { class: 'item row', style: 'padding:6px 8px' },
      h('div', { class: 'grow', style: 'min-width:0' },
        h('div', { style: 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap' },
          w.foreground ? h('span', { class: 'badge' }, '前台') : null, ' ', w.title || w.procName),
        h('div', { class: 'muted small ell' }, `${w.procName} · pid ${w.pid}`)),
      h('button', { class: 'ghost small', text: '前置' }),
      h('button', { class: 'ghost small', text: '关' }),
      h('button', { class: 'ghost small', text: '杀' }),
    );
    const btns = row.querySelectorAll<HTMLButtonElement>('button');
    btns[0].onclick = () => cmd('app.focus', { hwnd: w.hwnd }, 15_000).catch((e) => toast((e as Error).message));
    btns[1].onclick = () => cmd('app.close', { hwnd: w.hwnd }, 15_000).then(() => toast('已发送关闭', 'ok')).catch((e) => toast((e as Error).message));
    btns[2].onclick = () => {
      if (confirm(`强制结束 ${w.procName}（pid ${w.pid}）？`)) {
        cmd('app.close', { hwnd: w.hwnd, force: true }, 15_000).then(() => toast('已强杀', 'ok')).catch((e) => toast((e as Error).message));
      }
    };
    box.append(row);
  }
}

/* CC GUI 运行状态（快捷控制卡头部指示，ccgui.status 查询后原地更新） */
let ccguiStatusEl: HTMLElement | null = null;
/** 异步序号：晚到的 ccgui.status 结果丢弃（快速切 tab / 连点时防串台） */
let ccguiSeq = 0;

function renderCcguiStatus(running: boolean | null): void {
  const el = ccguiStatusEl;
  if (!el) return;
  clear(el);
  if (running === null) {
    el.textContent = 'CC GUI 状态未知';
    return;
  }
  el.append(h('span', { class: `dot ${running ? 'ok' : 'off'}`, style: 'margin:0' }), `CC GUI ${running ? '运行中' : '未运行'}`);
}

async function fetchCcguiStatus(): Promise<void> {
  if (!state.selected) return;
  const seq = ++ccguiSeq;
  let running: boolean | null = null;
  try {
    const o = await cmd('ccgui.status', {}, 20_000);
    if (seq !== ccguiSeq) return;
    running = (o.result.data as { running?: boolean } | undefined)?.running === true;
  } catch {
    if (seq !== ccguiSeq) return;
  }
  renderCcguiStatus(running);
}

/* 显示器电源状态（快捷控制卡头部指示，display.status 查询后原地更新） */
let displayStatusEl: HTMLElement | null = null;
let displaySeq = 0;

const DISPLAY_STATE_TEXT: Record<string, string> = { on: '显示器 开', dim: '显示器 变暗', off: '显示器 已关' };

function renderDisplayStatus(st: 'on' | 'dim' | 'off' | null): void {
  const el = displayStatusEl;
  if (!el) return;
  clear(el);
  if (st === null) {
    el.textContent = '显示器 状态未知';
    return;
  }
  el.append(h('span', { class: `dot ${st === 'off' ? 'off' : 'ok'}`, style: 'margin:0' }), DISPLAY_STATE_TEXT[st] ?? '显示器 未知');
}

async function fetchDisplayStatus(): Promise<void> {
  if (!state.selected) return;
  const seq = ++displaySeq;
  let st: 'on' | 'dim' | 'off' | null = null;
  try {
    const o = await cmd('display.status', {}, 20_000);
    if (seq !== displaySeq) return;
    const d = o.result.data as { state?: 'on' | 'dim' | 'off' } | undefined;
    st = d && (d.state === 'on' || d.state === 'dim' || d.state === 'off') ? d.state : null;
  } catch {
    if (seq !== displaySeq) return;
  }
  renderDisplayStatus(st);
}

/* 双通道状态行（快捷控制卡底部，channel.status 查询后原地更新） */
let channelStatusEl: HTMLElement | null = null;
let channelSeq = 0;

function channelSpan(label: string, cls: string, text: string): HTMLElement {
  // gap 统一「标签/绿点/文字」间距；.dot 自带 margin-right 会叠加，实例上归零
  return h('span', { class: 'muted small', style: 'display:inline-flex;align-items:center;gap:6px' },
    label, h('span', { class: `dot ${cls}`, style: 'margin:0' }), text);
}

function renderChannelStatus(d: ChannelStatusResponse | null): void {
  const el = channelStatusEl;
  if (!el) return;
  clear(el);
  if (!d) {
    el.textContent = '通道状态未知（查询失败）';
    return;
  }
  // AI-Mobile 通道：桥接 ↔ 云中继（RTT 为桥接侧心跳差值）
  const m = d.mobile;
  el.append(
    channelSpan('AI-Mobile 通道', m.connected ? 'ok' : 'err',
      m.connected ? `已连接${m.rttMs === null ? '' : `·${m.rttMs}ms`}` : '已断开'),
  );
  // AI-ccgui 通道：CC GUI 手机中转（中继可达 ≠ 桌面在线，分开展示）
  const c = d.ccgui;
  let cCls = 'err';
  let cText = '中继不可达';
  if (c.reachable) {
    if (c.agentOnline) {
      cCls = 'ok';
      cText = `正常${c.rttMs === null ? '' : `·${c.rttMs}ms`}`;
    } else {
      cCls = 'off';
      cText = '中继在线·CC GUI 未连接';
    }
  }
  el.append(channelSpan('AI-ccgui 通道', cCls, cText));
}

async function fetchChannelStatus(): Promise<void> {
  if (!state.selected) return;
  const seq = ++channelSeq;
  let d: ChannelStatusResponse | null = null;
  try {
    const o = await cmd('channel.status', {}, 25_000);
    if (seq !== channelSeq) return;
    d = o.result.data as ChannelStatusResponse;
  } catch {
    if (seq !== channelSeq) return;
  }
  renderChannelStatus(d);
}

function renderScreen(): HTMLElement {
  const wrap = h('div', {});

  /* 卡1：快捷控制（一键操作电脑） */
  const quickActions: [string, string, string, string?][] = [
    ['关闭显示器', 'power.monitorOff', '已关闭电脑显示器'],
    ['锁定电脑', 'power.lock', '已锁定电脑'],
    ['重启电脑', 'power.restart', '已请求重启，约 3 秒后开始',
      '确定重启电脑？\n\n约 3 秒后开始，远程连接会中断，且这 3 秒内无法取消。\n开机后 bridge 会自动恢复（前提是已配置自动登录 + 自启动）。'],
    ['关机', 'power.shutdown', '已请求关机，约 3 秒后开始',
      '确定关机？\n\n约 3 秒后开始，且这 3 秒内无法取消。\n关机后电脑不再通电，手机端无法再唤醒它，必须到电脑前手动开机。'],
  ];
  const powerRow = h('div', { class: 'key-chips' });
  for (const [label, action, okText, confirmText] of quickActions) {
    const b = h('button', { class: 'ghost small', text: label });
    // 二次确认（防误点，且不依赖 window.confirm —— APK WebView 无 onJsConfirm 会静默返回 false）：
    // 首次点击进入待确认态（变红 + 文案加「确认…？」），5 秒内再点才执行，超时自动还原
    let armTimer: ReturnType<typeof setTimeout> | undefined;
    const disarm = () => {
      clearTimeout(armTimer);
      b.classList.remove('danger');
      b.classList.add('ghost');
      b.textContent = label;
    };
    b.onclick = async () => {
      if (confirmText && !b.classList.contains('danger')) {
        b.classList.remove('ghost');
        b.classList.add('danger');
        b.textContent = `确认${label}？`;
        armTimer = setTimeout(disarm, 5000);
        return;
      }
      disarm();
      b.disabled = true;
      try {
        await cmd(action, {}, 20_000);
        toast(okText, 'ok');
        if (action === 'power.monitorOff') void fetchDisplayStatus();
      } catch (e) {
        toast((e as Error).message);
      } finally {
        b.disabled = false;
      }
    };
    powerRow.append(b);
  }
  // 启动 CC GUI 客户端：已在运行则只切前台，不会重复开
  const ccguiBtn = h('button', { class: 'ghost small', text: '启动 CC GUI' });
  ccguiBtn.onclick = async () => {
    ccguiBtn.disabled = true;
    try {
      const o = await cmd('ccgui.launch', {}, 30_000);
      const d = o.result.data as { started?: boolean; focused?: boolean } | undefined;
      toast(d?.started ? 'CC GUI 已启动' : d?.focused ? 'CC GUI 已在运行，已切到前台' : 'CC GUI 已在运行', 'ok');
      void fetchCcguiStatus();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      ccguiBtn.disabled = false;
    }
  };

  // 关闭 CC GUI 客户端：taskkill 整树，二次确认防误触
  const ccguiCloseBtn = h('button', { class: 'ghost small', text: '关闭 CC GUI' });
  ccguiCloseBtn.onclick = async () => {
    if (!confirm('确定关闭 CC GUI？')) return;
    ccguiCloseBtn.disabled = true;
    try {
      const o = await cmd('ccgui.close', {}, 30_000);
      const d = o.result.data as { closed?: boolean } | undefined;
      toast(d?.closed ? 'CC GUI 已关闭' : 'CC GUI 未在运行', 'ok');
      void fetchCcguiStatus();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      ccguiCloseBtn.disabled = false;
    }
  };

  // 状态回显：CC GUI / 显示器 / 双通道（探针模式下由 probe.data ctrl 层驱动原地更新；
  // 降级模式下进 tab 逐个查询）
  const ccguiChip = h('span', { class: 'muted small', style: 'display:inline-flex;align-items:center;gap:6px', text: 'CC GUI 查询中…' });
  ccguiStatusEl = ccguiChip;

  const displayChip = h('span', { class: 'muted small', style: 'display:inline-flex;align-items:center;gap:6px', text: '显示器 查询中…' });
  displayStatusEl = displayChip;

  // display:contents —— 两个通道项作为 statusRow 的直接 flex 项参与换行
  const channelSpans = h('span', { class: 'muted small', style: 'display:contents', text: '通道状态查询中…' });
  channelStatusEl = channelSpans;

  syncProbeWatch();
  if (probeDegraded) {
    void fetchCcguiStatus();
    void fetchDisplayStatus();
    void fetchChannelStatus();
  } else if (state.ctrl) {
    // 探针模式：已有 ctrl 帧则立即回显；尚无则保持「查询中…」，首帧 ctrl 到达后由 applyProbe 刷新
    rerenderCtrlChips();
  }

  // 功能分组网格：标签列 + 内容列，状态回显跟在对应按钮组下方（align-items:start 防按钮换行时标签居中漂移）
  // 按钮组行标签行高 34px 与按钮中线对齐；纯文本行（通道）用继承行高，避免标签相对内容下沉
  const qkLabel = (t: string, lh = '34px') => h('span', { class: 'muted small', style: `font-weight:600;line-height:${lh}` }, t);
  const qkGrid = h('div', { style: 'display:grid;grid-template-columns:max-content 1fr;gap:12px;align-items:start' },
    qkLabel('电源'),
    h('div', {}, powerRow, h('div', { style: 'margin-top:6px;display:flex' }, displayChip)),
    qkLabel('CC GUI'),
    h('div', {}, h('div', { class: 'key-chips' }, ccguiBtn, ccguiCloseBtn), h('div', { style: 'margin-top:6px;display:flex' }, ccguiChip)),
    qkLabel('通道', 'inherit'),
    // display:contents 的通道项参与内层 flex 换行，不破坏外层两列网格
    h('div', { style: 'display:flex;flex-wrap:wrap;align-items:center;gap:6px 16px' }, channelSpans),
  );

  const quickCard = h('div', { class: 'card' },
    h('div', { class: 'sec', style: 'margin-top:0' }, '快捷控制'),
    qkGrid,
  );

  /* 卡2：应用（启动程序 + 窗口管理） */
  const appListBox = h('div', { style: 'margin-top:8px' });
  screenAppListBox = appListBox;
  const refreshBtn = h('button', { class: 'ghost small', text: '刷新窗口列表' });
  refreshBtn.onclick = () => void loadWindows();
  const launchInput = h('input', { placeholder: '启动应用，如 notepad / calc / mspaint' });
  const launchBtn = h('button', { class: 'small', text: '启动' });
  launchBtn.onclick = async () => {
    const cmdStr = (launchInput as HTMLInputElement).value.trim();
    if (!cmdStr) {
      toast('请输入要启动的程序');
      return;
    }
    launchBtn.disabled = true;
    try {
      await cmd('app.launch', { cmd: cmdStr }, 20_000);
      toast(`已启动 ${cmdStr}`, 'ok');
      setTimeout(() => void loadWindows(), 1200);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      launchBtn.disabled = false;
    }
  };

  const appCard = h('div', { class: 'card' },
    h('div', { class: 'sec', style: 'margin-top:0' }, '应用'),
    h('div', { class: 'row', style: 'margin-top:4px' }, launchInput, launchBtn),
    h('div', { class: 'row', style: 'margin-top:8px' }, refreshBtn, h('span', { class: 'grow' })),
    appListBox,
  );

  wrap.append(quickCard, appCard);
  if (state.windows.length > 0) renderScreenAppList();
  return wrap;
}

/* --------------------- 直播（窗口/整屏实时回传，仅观看） --------------------- */

/** 直播目标：整屏 或 指定窗口（hwnd 来自 app.list） */
type LiveTarget = { kind: 'screen' } | { kind: 'window'; hwnd: number; title: string };

/** 当前选择的目标（切 tab / 断线重连保持，页面会话内有效） */
let liveTarget: LiveTarget = { kind: 'screen' };
/** 直播开启意图（true 才处理帧/续播；手动停止、切 tab、切设备、断开时置 false） */
let liveActive = false;
/** 最近一帧到达时间（0=本段尚未收到首帧；>3s 无帧显示中断提示） */
let liveLastFrameTs = 0;
let liveStallTimer: ReturnType<typeof setInterval> | null = null;
/** stopLive 递增；在途 startLive 完成时发现 token 变化 = 期间已被取消，补发 screen.stop */
let liveToken = 0;
/** startLive 在途（启动很慢时点「停止」也要发 screen.stop） */
let livePending = false;

/** 画面/状态挂载点（重进 tab 后重挂） */
let liveImgEl: HTMLImageElement | null = null;
let liveHintEl: HTMLElement | null = null;
let liveStatusEl: HTMLElement | null = null;
let liveWinListBox: HTMLElement | null = null;
let liveScreenChipEl: HTMLButtonElement | null = null;

function liveTargetText(): string {
  return liveTarget.kind === 'screen' ? '整屏' : liveTarget.title || `窗口 ${liveTarget.hwnd}`;
}

function setLiveStatus(text: string, cls: 'ok' | 'err' | 'off' = 'off'): void {
  if (!liveStatusEl) return;
  clear(liveStatusEl);
  liveStatusEl.append(h('span', { class: `dot ${cls}`, style: 'margin:0' }), text);
}

/** 状态行统一刷新：未开始 / 直播中 / 重连中 / 画面中断 */
function liveStatusRefresh(): void {
  if (!liveActive) {
    setLiveStatus('未开始');
    return;
  }
  if (!client.isOpen) {
    setLiveStatus('连接断开，重连中…', 'err');
    return;
  }
  if (liveLastFrameTs > 0 && Date.now() - liveLastFrameTs > 3000) {
    setLiveStatus('画面中断（窗口可能已关闭/最小化，或网络异常）', 'err');
    return;
  }
  setLiveStatus(`直播中·${liveTargetText()}`, 'ok');
}

/** 帧停滞检测（每秒刷新状态行；liveActive=false 时静默） */
function ensureLiveStallTimer(): void {
  if (liveStallTimer) return;
  liveStallTimer = setInterval(() => {
    if (liveActive) liveStatusRefresh();
  }, 1000);
}

function renderLiveWinList(): void {
  const box = liveWinListBox;
  if (!box) return;
  clear(box);
  if (state.windows.length === 0) {
    box.append(h('div', { class: 'muted small', style: 'margin-top:6px' }, '没有可见窗口（点「刷新窗口列表」枚举）'));
    return;
  }
  for (const w of state.windows.slice(0, 40)) {
    const sel = liveTarget.kind === 'window' && liveTarget.hwnd === w.hwnd;
    const row = h('div', { class: 'item row', style: `padding:6px 8px;cursor:pointer${sel ? ';background:rgba(79,124,255,.08);border-radius:8px' : ''}` },
      h('div', { class: 'grow', style: 'min-width:0' },
        h('div', { class: 'ell' },
          w.foreground ? h('span', { class: 'badge' }, '前台') : null, ' ', w.title || w.procName),
        h('div', { class: 'muted small ell' }, `${w.procName} · pid ${w.pid}`)),
      sel ? h('span', { class: 'badge' }, '直播中') : h('span', { class: 'muted small', style: 'flex:0 0 auto' }, '选为直播'),
    );
    row.onclick = () => void switchLiveTarget({ kind: 'window', hwnd: w.hwnd, title: w.title || w.procName });
    box.append(row);
  }
}

function liveTargetsRefresh(): void {
  liveScreenChipEl?.classList.toggle('on', liveTarget.kind === 'screen');
  renderLiveWinList();
}

/** 切换直播目标：未开播只记选择；已开播直接重启截取（pc-agent 截取循环内无缝换 cfg） */
async function switchLiveTarget(t: LiveTarget): Promise<void> {
  liveTarget = t;
  liveTargetsRefresh();
  if (liveActive) await startLive();
  else liveStatusRefresh();
}

async function startLive(): Promise<void> {
  if (!state.selected) return;
  const token = liveToken;
  livePending = true;
  setLiveStatus(liveActive ? '切换中…' : '启动中…（首次可能较慢）');
  try {
    const params: Record<string, unknown> = {};
    if (liveTarget.kind === 'window') params.hwnd = liveTarget.hwnd;
    const o = await cmd('screen.start', params, 45_000);
    if (liveToken !== token) {
      // 期间已被停止/切走：刚启动的截取补发停止，避免孤儿流
      if (state.selected) client.cmdNoWait(state.selected, 'screen.stop');
      return;
    }
    const d = o.result.data as ScreenStartResult | undefined;
    if (d?.mode === 'window') {
      const prev = liveTarget.kind === 'window' ? liveTarget.title : '';
      liveTarget = { kind: 'window', hwnd: typeof d.hwnd === 'number' ? d.hwnd : 0, title: d.title || prev };
    }
    liveActive = true;
    liveLastFrameTs = 0;
    if (liveHintEl) {
      liveHintEl.style.display = 'flex';
      liveHintEl.textContent = '等待首帧…';
    }
    ensureLiveStallTimer();
    liveTargetsRefresh();
    liveStatusRefresh();
  } catch (e) {
    if (liveToken === token) {
      liveActive = false;
      setLiveStatus((e as Error).message, 'err');
    }
  } finally {
    livePending = false;
  }
}

/** 停止直播（幂等；用于手动停止 / 切 tab / 切设备 / 断开） */
function stopLive(): void {
  liveToken++; // 使在途 startLive 失效（其完成时会补发停止）
  const had = liveActive || livePending;
  liveActive = false;
  liveLastFrameTs = 0;
  if (liveImgEl) liveImgEl.removeAttribute('src');
  if (liveHintEl) {
    liveHintEl.style.display = 'flex';
    liveHintEl.textContent = '选择目标后点「开始直播」';
  }
  // 发即忘：断线/桥接不在线时静默；bridge 断云时也会自行停采
  if (had && state.selected) client.cmdNoWait(state.selected, 'screen.stop');
  liveStatusRefresh();
}

function renderLive(): HTMLElement {
  const wrap = h('div', {});

  /* 卡1：直播目标（整屏 / 指定窗口） */
  const screenChip = h('button', { class: 'chip', text: '整屏' });
  liveScreenChipEl = screenChip;
  screenChip.onclick = () => void switchLiveTarget({ kind: 'screen' });
  const refreshBtn = h('button', { class: 'ghost small', text: '刷新窗口列表' });
  refreshBtn.onclick = () => void loadWindows();
  const winBox = h('div', { style: 'margin-top:8px' });
  liveWinListBox = winBox;

  const targetCard = h('div', { class: 'card' },
    h('div', { class: 'sec', style: 'margin-top:0' }, '直播目标'),
    h('div', { class: 'row' }, screenChip, refreshBtn, h('span', { class: 'grow' })),
    winBox,
  );

  /* 卡2：画面 */
  liveStatusEl = h('div', { class: 'row', style: 'margin:4px 0 8px' });
  const toggleBtn = h('button', { class: 'small', text: liveActive ? '停止直播' : '开始直播' });
  toggleBtn.onclick = async () => {
    if (liveActive) {
      stopLive();
      toggleBtn.textContent = '开始直播';
      return;
    }
    toggleBtn.textContent = '停止直播';
    await startLive();
    if (!liveActive) toggleBtn.textContent = '开始直播'; // 启动失败还原
  };
  const surface = h('div', { class: 'screen-surface', style: 'margin-top:10px' });
  const img = h('img', { alt: '实时画面' });
  liveImgEl = img;
  const hint = h('div', { class: 'live-hint muted small' }, liveActive ? '等待首帧…' : '选择目标后点「开始直播」');
  liveHintEl = hint;
  surface.append(img, hint);

  const viewCard = h('div', { class: 'card' },
    h('div', { class: 'sec', style: 'margin-top:0' }, '画面'),
    toggleBtn,
    liveStatusEl,
    surface,
  );

  wrap.append(targetCard, viewCard);
  if (state.windows.length === 0) void loadWindows(); // 首次进入自动枚举（loadWindows 内失败会 toast）
  liveTargetsRefresh();
  liveStatusRefresh();
  return wrap;
}

/* ------------------------------ 系统监控（sys.monitor） ------------------------------ */

/** Win32_Battery.BatteryStatus → 中文（WMI 常见实际值：1=放电 2=交流） */
const BATTERY_STATUS: Record<number, string> = {
  1: '使用电池中',
  2: '已接通电源',
  3: '已充满',
  4: '电量低',
  5: '电量严重低',
  6: '充电中',
  7: '充电中·电量高',
  8: '充电中·电量低',
  9: '充电中·电量严重低',
  11: '部分充电',
};
/** 接通电源 / 充电中的状态码 → 图标 */
const BATTERY_PLUG: Record<number, string> = { 2: '🔌', 6: '⚡', 7: '⚡', 8: '⚡', 9: '⚡' };

/** 面板容器（探针帧 / sys.monitor 结果到达后局部重绘，避免整页 render()） */
let monitorPanelEl: HTMLElement | null = null;
/** 异步序号：晚到的 sys.monitor 结果丢弃（快速切设备/切 tab 时防串台） */
let monitorSeq = 0;

async function fetchMonitor(): Promise<void> {
  if (!state.selected) return;
  const seq = ++monitorSeq;
  try {
    const o = await cmd('sys.monitor', {}, 20_000);
    if (seq !== monitorSeq) return;
    state.monitor = o.result.data as SysMonitorResponse;
    state.monitorErr = '';
  } catch (e) {
    if (seq !== monitorSeq) return;
    state.monitorErr = (e as Error).message;
  }
  rerenderMonitor();
}

/* --------------------- 探针（probe.watch → probe.data 推送） --------------------- */

const WATCH_TTL_MS = 30_000;
const RENEW_MS = 15_000; // TTL 的一半：容忍一次续期丢失
/** 是否已发 probe.watch 且未停（含续期定时器） */
let probeWatching = false;
let probeRenewTimer: ReturnType<typeof setInterval> | null = null;
/** bridge 不支持 probe.watch（旧版 bridge「未知 action」/ 旧 server 白名单丢弃超时）→ 降级旧 5s 轮询；刷新页面重试探针 */
let probeDegraded = false;
/** ctrl 层 ts 单调检查：动作后手动 fetchDisplayStatus 等即时结果不被更旧的探针帧回退 */
let lastCtrlTs = 0;
/** 降级模式的 5s 轮询定时器 */
let legacyTimer: ReturnType<typeof setInterval> | null = null;

function stopLegacyPolling(): void {
  if (legacyTimer) {
    clearInterval(legacyTimer);
    legacyTimer = null;
  }
}

function startLegacyPolling(): void {
  if (legacyTimer) return;
  legacyTimer = setInterval(() => {
    if (state.tab !== 'monitor' || !state.connected) {
      stopLegacyPolling();
      return;
    }
    void fetchMonitor();
  }, 5000);
}

function stopProbeRenew(): void {
  if (probeRenewTimer) {
    clearInterval(probeRenewTimer);
    probeRenewTimer = null;
  }
}

function probeDesired(): boolean {
  return state.connected && !!state.selected && (state.tab === 'monitor' || state.tab === 'screen');
}

/** 按需同步 watch：进入监控/快捷控制 tab 时开（两 tab 共享一个 watch），离开时关。可安全重复调用 */
function syncProbeWatch(): void {
  const want = probeDesired() && !probeDegraded;
  if (want && !probeWatching) {
    probeWatching = true;
    cmd('probe.watch', { ttlMs: WATCH_TTL_MS }, 15_000)
      .then((o) => {
        const d = o.result.data as ProbeData | undefined;
        if (d?.fast) applyProbe(d); // 首帧快照立即渲染，不等第一个 2s 采样
      })
      .catch((e) => {
        const msg = (e as Error).message ?? '';
        probeWatching = false;
        stopProbeRenew();
        if (msg.includes('设备离线')) return; // 暂时离线：等状态回调触发 syncProbeWatch 重试
        probeDegraded = true; // 旧 bridge / 旧 server → 降级
        startLegacyPolling();
      });
    probeRenewTimer = setInterval(() => {
      if (state.selected) client.cmdNoWait(state.selected, 'probe.watch', { ttlMs: WATCH_TTL_MS });
    }, RENEW_MS);
  } else if (!want && probeWatching) {
    if (state.selected) client.cmdNoWait(state.selected, 'probe.unwatch');
    stopProbeWatch(false);
  }
}

function stopProbeWatch(clearData: boolean): void {
  probeWatching = false;
  stopProbeRenew();
  if (clearData) {
    state.monitor = null;
    state.monitorErr = '';
    state.monitorSlowTs = 0;
    state.ctrl = null;
  }
}

/** 探针帧合并：fast/slow 合成进 state.monitor（保持 SysMonitorResponse 形状），ctrl 单独存 */
function applyProbe(d: ProbeData): void {
  if (!state.connected || !state.selected) return;
  const m: SysMonitorResponse = state.monitor ?? {
    cpu: { model: '', cores: 0, usage: 0 },
    mem: { total: 0, free: 0 },
    disks: [],
    battery: null,
    claudeSessions: 0,
    uptime: 0,
  };
  if (d.fast.cpuUsage !== null) m.cpu.usage = d.fast.cpuUsage;
  m.mem = d.fast.mem;
  if (d.slow) {
    m.cpu.model = d.slow.cpuModel;
    m.cpu.cores = d.slow.cpuCores;
    m.disks = d.slow.disks;
    m.battery = d.slow.battery;
    m.claudeSessions = d.slow.claudeSessions;
    m.uptime = d.slow.uptime;
    state.monitorSlowTs = d.slow.ts;
  }
  state.monitor = m;
  state.monitorErr = '';
  if (d.ctrl && d.ctrl.ts >= lastCtrlTs) {
    lastCtrlTs = d.ctrl.ts;
    state.ctrl = { display: d.ctrl.display, ccgui: d.ctrl.ccgui, channel: d.ctrl.channel };
  }
  if (state.tab === 'monitor') rerenderMonitor();
  else if (state.tab === 'screen') rerenderCtrlChips();
}

/** 快捷控制三状态 chip 由探针 ctrl 层驱动（tab 内原地更新，离开 tab 后元素悬空由 renderXxx 兜底） */
function rerenderCtrlChips(): void {
  renderCcguiStatus(state.ctrl?.ccgui?.running ?? null);
  renderDisplayStatus(state.ctrl?.display?.state ?? null);
  renderChannelStatus(state.ctrl?.channel ?? null);
}

function rerenderMonitor(): void {
  if (monitorPanelEl && state.tab === 'monitor') {
    clear(monitorPanelEl);
    monitorPanelEl.append(monitorContent());
  }
}

function fmtUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d} 天 ${h} 小时`;
  if (h > 0) return `${h} 小时 ${m} 分`;
  return `${m} 分钟`;
}

/** 用量进度条：≥80% 橙、≥90% 红 */
function barOf(pct: number): HTMLElement {
  const p = Math.max(0, Math.min(100, pct));
  const cls = p >= 90 ? 'bar high' : p >= 80 ? 'bar warn' : 'bar';
  return h('div', { class: cls }, h('i', { style: `width:${p.toFixed(1)}%` }));
}

/** 单行监控项：标题 + 数值 + 进度条 + 说明行 */
function monitorRow(label: string, valueText: string, pct?: number, sub?: string): HTMLElement {
  return h('div', { class: 'mon-row' },
    h('div', { class: 'row' },
      h('span', { class: 'small', style: 'font-weight:600', text: label }),
      h('span', { class: 'grow' }),
      h('span', { class: 'small', text: valueText })),
    pct === undefined ? null : barOf(pct),
    sub ? h('div', { class: 'muted small ell', text: sub }) : null,
  );
}

function monitorContent(): HTMLElement {
  const box = h('div', { class: 'card' });

  // 探针推送天然实时，无需自动刷新开关；「刷新」保留为手动兜底（legacy cmd，降级模式下也用它）
  const refreshBtn = h('button', { class: 'ghost small', text: '刷新' });
  refreshBtn.onclick = () => void fetchMonitor();

  box.append(
    h('div', { class: 'row', style: 'margin-top:0' },
      h('span', { class: 'sec inline' }, '系统监控'),
      h('span', { class: 'grow' }),
      refreshBtn,
    ),
  );

  if (state.monitorErr) {
    box.append(h('div', { class: 'err small', style: 'margin-top:8px' }, `⚠ ${state.monitorErr}`));
    return box;
  }
  const m = state.monitor;
  if (!m) {
    box.append(h('div', { class: 'muted', style: 'margin-top:8px' }, '加载中…'));
    return box;
  }

  // 慢层（磁盘/电量/会话数，30s 一次 PowerShell 采样）未到达时不渲染占位零值；
  // 降级模式走 sys.monitor 一次性全量数据，无慢层概念
  const slowReady = probeDegraded || state.monitorSlowTs > 0;

  // CPU
  box.append(monitorRow('CPU', `${m.cpu.usage.toFixed(0)}%`, m.cpu.usage,
    slowReady ? `${m.cpu.model} · ${m.cpu.cores} 线程` : '采集中…'));

  // 内存
  const memUsed = m.mem.total - m.mem.free;
  const memPct = m.mem.total > 0 ? (memUsed / m.mem.total) * 100 : 0;
  box.append(monitorRow('内存', `${memPct.toFixed(0)}%`, memPct,
    `已用 ${fmtSize(memUsed)} / 共 ${fmtSize(m.mem.total)}`));

  if (!slowReady) {
    box.append(h('div', { class: 'muted small', style: 'margin-top:4px' },
      '磁盘 / 电量 / Claude 会话采集中…'));
    return box;
  }

  // 磁盘（每个盘符一行）
  for (const d of m.disks) {
    const dPct = d.total > 0 ? ((d.total - d.free) / d.total) * 100 : 0;
    box.append(monitorRow(`磁盘 ${d.name}`, `${dPct.toFixed(0)}%`, dPct,
      `剩余 ${fmtSize(d.free)} / 共 ${fmtSize(d.total)}`));
  }
  if (m.disks.length === 0) {
    box.append(h('div', { class: 'muted small' }, '未查询到本地磁盘'));
  }

  // 电量（台式机无电池）
  const b = m.battery;
  box.append(monitorRow('电量', b ? `${BATTERY_PLUG[b.status] ?? '🔋'} ${b.percent}%` : '—',
    b ? 100 - b.percent : undefined,
    b ? BATTERY_STATUS[b.status] ?? `状态 ${b.status}` : '未检测到电池（台式机）'));

  // Claude 会话（运行中的 claude 进程数）
  box.append(monitorRow('Claude 会话', `${m.claudeSessions} 个运行中`,
    undefined, m.claudeSessions > 0 ? '正在运行的 Claude Code / happy 会话' : '当前没有运行中的 Claude 会话'));

  box.append(h('div', { class: 'muted small', style: 'margin-top:6px' },
    `开机时长 ${fmtUptime(m.uptime)}`));
  return box;
}

function renderMonitor(): HTMLElement {
  const wrap = h('div', {});
  monitorPanelEl = wrap;
  if (probeDegraded) {
    // 降级（旧 bridge/server）：恢复旧 5s 轮询 + 进入时拉一次
    startLegacyPolling();
    if (!state.monitor && !state.monitorErr) void fetchMonitor();
  } else {
    // 探针模式：watch 幂等同步（重复调用 no-op），数据由 probe.data 推送驱动
    syncProbeWatch();
  }
  rerenderMonitor();
  return wrap;
}

function currentDevice(): DeviceStatus | undefined {
  return state.devices.find((d) => d.id === state.selected) ?? state.devices[0];
}

/** 在线设备列表：离线机不上下拉/设备页条目，状态由顶部面板回显 */
function onlineDevices(): DeviceStatus[] {
  return state.devices.filter((d) => d.online);
}

function onlineDot(online: boolean): HTMLElement {
  return h('span', { class: `dot ${online ? 'ok' : 'off'}` });
}

/* ------------------------------ 文件浏览（只读 + fs.watch 实时刷新） ------------------------------ */

/** 面板内容容器（fs.event 到达后局部重绘，避免整页 render()） */
let filesPanelEl: HTMLElement | null = null;
/** 异步序号：晚到的 fs.list/fs.read 结果丢弃（快速连续点目录时防串台） */
let fsReqSeq = 0;

function fsBasename(p: string): string {
  const i = p.lastIndexOf('\\');
  return i === -1 ? p : p.slice(i + 1) || p;
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(ms: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const p = (v: number): string => String(v).padStart(2, '0');
  const hm = `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  return d.getFullYear() === new Date().getFullYear() ? hm : `${d.getFullYear()}-${hm.slice(0, 5)}`;
}

/** 停止目录监视（保留已缓存的目录/文件状态，回 tab 时重新 watch + 拉取） */
async function stopFsWatch(): Promise<void> {
  try {
    await cmd('fs.watch.stop', {}, 15_000);
  } catch {
    /* 设备可能已离线，忽略 */
  }
}

/** 切设备/断开时彻底清空文件浏览状态（含持久化的浏览位置） */
function resetFsState(): void {
  fsReqSeq++;
  fsBackStack.length = 0;
  save(LS.fsdir, '');
  save(LS.fsfile, '');
  state.fsPath = '';
  state.fsParent = null;
  state.fsEntries = [];
  state.fsViewing = null;
  state.fsLoading = false;
}

/** 关闭文件查看器（回到目录列表，清除持久化的文件位置） */
function fsCloseViewer(): void {
  state.fsViewing = null;
  save(LS.fsfile, '');
}

/** 返回上一页：查看文件→回目录列表；目录→回浏览历史的上一级（无历史则回上级目录） */
function fsGoBack(): void {
  if (state.fsViewing) {
    fsCloseViewer();
    rerenderFiles();
    return;
  }
  // 弹出与当前目录不同的上一个位置
  let prev = fsBackStack.pop();
  while (prev && prev.toLowerCase() === state.fsPath.toLowerCase()) prev = fsBackStack.pop();
  if (prev) void fsEnterDir(prev, true);
  else if (state.fsParent) void fsEnterDir(state.fsParent, true);
}

/** 文件 tab 侧滑手势：横向滑动返回上一页。代码区内仅右滑生效（左滑留给代码横向滚动），
 *  且滑动确实引起横向滚动时视为浏览代码而非手势。 */
function attachFsSwipe(el: HTMLElement): void {
  let sx = 0;
  let sy = 0;
  let codeEl: HTMLElement | null = null;
  let codeScrollLeft = 0;
  el.addEventListener('touchstart', (e) => {
    const t = e.touches[0];
    sx = t.clientX;
    sy = t.clientY;
    const target = e.target instanceof HTMLElement ? e.target : null;
    codeEl = (target?.closest('.codeview') as HTMLElement | null) ?? null;
    codeScrollLeft = codeEl ? codeEl.scrollLeft : 0;
  }, { passive: true });
  el.addEventListener('touchend', (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - sx;
    const dy = t.clientY - sy;
    // 横向滑动 ≥60px 且横向位移明显大于纵向（避开竖向列表滚动）
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (codeEl) {
      if (codeEl.scrollLeft !== codeScrollLeft || dx < 0) return;
    }
    fsGoBack();
  }, { passive: true });
}

/** 目录导航历史（侧滑返回用；模块级跨重绘存活） */
const fsBackStack: string[] = [];

/** 进入目录：拉列表 + 切换 watch 到新目录（isBack=true 表示返回导航，不记录历史） */
async function fsEnterDir(path: string, isBack = false): Promise<void> {
  if (!isBack && state.fsPath && state.fsPath.toLowerCase() !== path.toLowerCase()) {
    fsBackStack.push(state.fsPath);
  }
  const seq = ++fsReqSeq;
  state.fsViewing = null;
  save(LS.fsfile, ''); // 换目录即关闭文件视图
  state.fsLoading = true;
  rerenderFiles();
  try {
    const out = await cmd('fs.list', { path });
    if (seq !== fsReqSeq) return;
    const data = out.result.data as FsListResponse;
    state.fsPath = data.path;
    state.fsParent = data.parent;
    state.fsEntries = data.entries;
    save(LS.fsdir, data.path); // 持久化浏览位置（整页刷新后恢复）
    // re-watch 新目录（失败不阻塞浏览，只是没有实时刷新）
    void cmd('fs.watch.start', { path: data.path }, 15_000).catch(() => {
      /* 无权限等场景静默降级 */
    });
  } catch (e) {
    toast((e as Error).message);
  } finally {
    if (seq === fsReqSeq) state.fsLoading = false;
    rerenderFiles();
  }
}

/** 重新拉当前目录列表（刷新按钮 / fs.event） */
async function fsRefreshList(): Promise<void> {
  if (!state.fsPath || state.fsLoading) return;
  const seq = ++fsReqSeq;
  try {
    const out = await cmd('fs.list', { path: state.fsPath });
    if (seq !== fsReqSeq) return;
    const data = out.result.data as FsListResponse;
    state.fsEntries = data.entries;
  } catch (e) {
    toast((e as Error).message);
  } finally {
    if (seq === fsReqSeq) rerenderFiles();
  }
}

/** 打开文件查看器 */
async function fsOpenFile(path: string): Promise<void> {
  const seq = ++fsReqSeq;
  state.fsViewing = { path, text: '', truncated: false, size: 0 };
  state.fsLoading = true;
  rerenderFiles();
  try {
    const out = await cmd('fs.read', { path });
    if (seq !== fsReqSeq) return;
    const data = out.result.data as FsReadResponse;
    state.fsViewing = { path: data.path, text: data.text, truncated: data.truncated, size: data.size };
    save(LS.fsfile, data.path); // 持久化（整页刷新后恢复到该文件）
  } catch (e) {
    fsCloseViewer();
    toast((e as Error).message);
  } finally {
    if (seq === fsReqSeq) state.fsLoading = false;
    rerenderFiles();
  }
}

/** 静默重读当前查看的文件（fs.event 变更 / 面板重绘），失败不打扰 */
async function fsRefreshFile(): Promise<void> {
  const v = state.fsViewing;
  if (!v || state.fsLoading) return;
  try {
    const out = await cmd('fs.read', { path: v.path });
    const d = out.result.data as FsReadResponse;
    // 期间可能已返回目录或换了文件，只更新仍是同一文件的场景
    if (state.fsViewing && state.fsViewing.path.toLowerCase() === d.path.toLowerCase()) {
      state.fsViewing = { path: d.path, text: d.text, truncated: d.truncated, size: d.size };
      rerenderFiles();
    }
  } catch {
    /* 文件可能刚被删除，目录列表刷新会体现 */
  }
}

function rerenderFiles(): void {
  if (filesPanelEl && state.tab === 'files') {
    clear(filesPanelEl);
    filesPanelEl.append(fsContent());
  }
}

/** 面包屑：按 \ 分段，点击跳到该目录 */
function fsCrumbs(path: string): HTMLElement {
  const box = h('div', { class: 'crumbs' });
  const parts = path.split('\\');
  let acc = '';
  parts.forEach((p, i) => {
    acc = acc ? `${acc}\\${p}` : p;
    const target = acc;
    const seg = h('button', { class: 'crumb', text: p || '\\' });
    seg.onclick = () => {
      if (target !== state.fsPath) void fsEnterDir(target);
    };
    box.append(seg, h('span', { class: 'crumb-sep', text: i < parts.length - 1 ? '\\' : '' }));
  });
  return box;
}

/** 文件 tab 面板内容（目录列表 或 文件查看器）：顶部单行控制条 + 内部滚动的展示区 */
function fsContent(): HTMLElement {
  const box = h('div', { class: 'card files' });

  // 文件查看器
  const v = state.fsViewing;
  if (v) {
    const back = h('button', { class: 'ghost small', text: '‹ 目录' });
    back.onclick = () => {
      fsCloseViewer();
      rerenderFiles();
    };
    box.append(
      h('div', { class: 'row fs-ctrl' },
        back,
        h('div', { class: 'grow ell', text: fsBasename(v.path) }),
        v.truncated ? h('span', { class: 'badge', text: `前 ${fmtSize(v.text.length)} / 共 ${fmtSize(v.size)}` }) : null,
        h('span', { class: 'muted small', text: fmtSize(v.size) }),
      ),
      h('div', { class: 'fs-scroll' },
        state.fsLoading && !v.text
          ? h('div', { class: 'muted', text: '加载中…' })
          : renderCode(v.text, fsBasename(v.path))),
    );
    return box;
  }

  // 目录列表：单行控制条（上级 + 面包屑 + 刷新 + 计数）
  const refreshBtn = h('button', { class: 'ghost small', text: '刷新' });
  refreshBtn.onclick = () => void fsRefreshList();
  const upBtn = h('button', { class: 'ghost small', text: '↑', title: '上级目录' });
  upBtn.onclick = () => {
    if (state.fsParent) void fsEnterDir(state.fsParent);
  };

  const scroll = h('div', { class: 'fs-scroll' });
  if (state.fsLoading && state.fsEntries.length === 0) {
    scroll.append(h('div', { class: 'muted', text: '加载中…' }));
  } else if (state.fsEntries.length === 0) {
    scroll.append(h('div', { class: 'muted', text: '空目录' }));
  } else {
    for (const e of state.fsEntries) {
      const row = h('div', { class: 'item row fs-entry' },
        h('span', { class: 'fs-ico', text: e.dir ? '📁' : '📄' }),
        h('div', { class: 'grow' },
          h('div', { class: 'ell', text: e.name }),
          h('div', { class: 'muted small ell' },
            `${e.dir ? '目录' : fmtSize(e.size)} · ${fmtDate(e.mtime)}`)),
      );
      row.onclick = () => {
        const p = `${state.fsPath}\\${e.name}`;
        if (e.dir) void fsEnterDir(p);
        else void fsOpenFile(p);
      };
      scroll.append(row);
    }
  }

  box.append(
    h('div', { class: 'row fs-ctrl' },
      state.fsParent ? upBtn : null,
      h('div', { class: 'fs-crumbs grow' }, fsCrumbs(state.fsPath || '…')),
      refreshBtn,
      h('span', { class: 'muted small', style: 'flex:0 0 auto', text: `${state.fsEntries.length}` }),
    ),
    scroll,
  );
  return box;
}

function renderFiles(): HTMLElement {
  const wrap = h('div', { class: 'files-wrap' });
  filesPanelEl = wrap;
  attachFsSwipe(wrap); // 侧滑返回（手势绑在容器上，内容重绘不影响）
  if (!state.fsPath) {
    // 首次进入：优先恢复上次浏览的目录（下拉刷新/重开 PWA 不丢位置），否则用工作目录
    const saved = load(LS.fsdir);
    const ws = state.status?.workspaceDir;
    const start = saved || ws;
    if (start) {
      void fsEnterDir(start).then(() => {
        if (!state.fsPath && ws && ws.toLowerCase() !== start.toLowerCase()) {
          void fsEnterDir(ws); // 上次浏览的目录已不存在，回退工作目录
          return;
        }
        const f = load(LS.fsfile);
        if (f && state.tab === 'files' && state.fsPath) void fsOpenFile(f); // 恢复上次打开的文件
      });
    } else {
      wrap.append(h('div', { class: 'card muted' }, '正在获取工作目录…'));
      void refreshStatus().then(() => {
        const dir = state.status?.workspaceDir;
        if (dir && state.tab === 'files' && !state.fsPath) void fsEnterDir(dir);
        else if (!dir && state.tab === 'files') rerenderFiles();
      });
    }
  } else {
    // 回到 tab / 下拉刷新重绘：保留正在查看的文件，静默刷新列表 + 重新 watch
    void cmd('fs.watch.start', { path: state.fsPath }, 15_000).catch(() => {
      /* 无权限等场景静默降级 */
    });
    void fsRefreshList();
    if (state.fsViewing) void fsRefreshFile();
  }
  return wrap;
}

/* ------------------------------ 视图：连接 ------------------------------ */

/* ------------------------------ 设置：桌面悬浮窗 ------------------------------ */

function renderSettings(): HTMLElement {
  const box = h('div', { class: 'card' });
  const n = native();
  if (!n?.setFloatingConfig) {
    box.append(h('div', { class: 'muted' }, '桌面悬浮窗需在 AI-Mobile App 内使用（浏览器预览不可用）。'));
    return box;
  }

  const st = h('div', { class: 'muted small', style: 'flex:0 0 auto;max-width:60%;text-align:right', text: '查询中…' });
  floatStatusEl = st;

  const toggleBtn = h('button', { text: '开启悬浮窗' });
  const permBtn = h('button', { class: 'ghost small', text: '打开悬浮窗权限设置', style: 'display:none' });
  permBtn.onclick = () => n.openOverlaySettings?.();

  toggleBtn.onclick = async () => {
    // 已开启 → 关闭
    if (toggleBtn.dataset.on === '1') {
      await floatCall((cb) => n.setFloatingConfig!(cb, JSON.stringify({ enabled: false })));
      save(LS.floatEnabled, '');
      toggleBtn.dataset.on = '';
      toggleBtn.textContent = '开启悬浮窗';
      setFloatStatus('悬浮窗已关闭');
      return;
    }
    // 开启流程：自检权限 →（缺通知权限先请求）→ 下发配置启动
    toggleBtn.disabled = true;
    const s = await floatCall((cb) => n.getFloatingState!(cb));
    if (!s.canDrawOverlays) {
      toggleBtn.disabled = false;
      permBtn.style.display = '';
      setFloatStatus('需要「显示悬浮窗」权限：点下方按钮授权后返回再点开启', 'err');
      return;
    }
    if (!s.notifGranted && n.requestNotificationPermission) {
      const g = await floatCall((cb) => n.requestNotificationPermission!(cb));
      if (!g.granted) {
        toggleBtn.disabled = false;
        setFloatStatus('未授通知权限：悬浮窗仍可运行，但系统可能限制后台驻留', 'err');
        return;
      }
    }
    const r = await floatCall((cb) => n.setFloatingConfig!(cb, floatPayload(true)));
    toggleBtn.disabled = false;
    if (r.ok) {
      save(LS.floatEnabled, '1');
      toggleBtn.dataset.on = '1';
      toggleBtn.textContent = '关闭悬浮窗';
      setFloatStatus('悬浮窗已启动，正在连接…');
    } else if (r.code === 'overlay') {
      permBtn.style.display = '';
      setFloatStatus('需要「显示悬浮窗」权限：点下方按钮授权后返回再点开启', 'err');
    } else if (r.code === 'notification') {
      setFloatStatus('需要通知权限：请再点一次开启并在弹窗中允许', 'err');
    } else {
      setFloatStatus(`启动失败：${r.error ?? '未知错误'}`, 'err');
    }
  };

  // 监控设备下拉（悬浮窗独立于主界面选择；默认跟随当前选中设备）
  const devSel = h('select', { style: 'flex:1;min-width:0' }) as HTMLSelectElement;
  for (const d of state.devices) devSel.append(h('option', { value: d.id, text: d.name || d.id }));
  devSel.value = floatDeviceId();
  if (!devSel.value && state.selected) devSel.value = state.selected;
  devSel.onchange = () => {
    save(LS.floatDevice, devSel.value);
    // 运行中切设备：立即下发（Service 端仅重订阅不断连接）
    if (toggleBtn.dataset.on === '1') {
      void floatCall((cb) => n.setFloatingConfig!(cb, floatPayload(true)));
    }
  };

  // 透明度滑块：拖动实时生效（50ms 节流，肉眼连续），松手持久化；0 = 全透明
  const opacityVal = h('span', { class: 'muted small', style: 'flex:0 0 auto', text: `${floatOpacityVal()}%` });
  const range = h('input', { type: 'range', min: '0', max: '100', step: '5', style: 'flex:1;min-width:0' }) as HTMLInputElement;
  const zeroHint = h('div', { class: 'muted small', style: 'display:none;margin-top:4px',
    text: '已全透明：未开触摸穿透时，隐形面板仍会拦截该区域点击（上方可开启）' });
  const setOpacityUi = (v: number) => {
    range.value = String(v);
    range.style.setProperty('--fill', `${v}%`);
    opacityVal.textContent = v === 0 ? '全透明' : `${v}%`;
    zeroHint.style.display = v === 0 ? '' : 'none';
  };
  setOpacityUi(floatOpacityVal());
  let lastSent = 0;
  range.oninput = () => {
    const v = parseInt(range.value, 10);
    setOpacityUi(v);
    const now = Date.now();
    if (now - lastSent > 50) {
      lastSent = now;
      n.setFloatingOpacity?.(v);
    }
  };
  range.onchange = () => {
    const v = parseInt(range.value, 10);
    save(LS.floatOpacity, String(v));
    n.setFloatingOpacity?.(v);
  };

  // 面板内容选择：勾选即时下发生效 + 持久化
  const rowChecks = new Map<string, HTMLInputElement>();
  const rowsBox = h('div', { class: 'row', style: 'flex-wrap:wrap;gap:4px 10px;margin-top:8px' });
  for (const [key, label] of FLOAT_ROWS_ALL) {
    const cb = h('input', { type: 'checkbox', id: `floatrow_${key}` }) as HTMLInputElement;
    cb.checked = floatRowsVal().includes(key);
    rowChecks.set(key, cb);
    cb.onchange = () => {
      const cur = FLOAT_ROWS_ALL.filter(([k]) => rowChecks.get(k)?.checked).map(([k]) => k);
      save(LS.floatRows, cur.join(','));
      void floatCall((c) => n.setFloatingRows!(c, JSON.stringify({ rows: cur })));
    };
    rowsBox.append(cb, h('label', { for: `floatrow_${key}`, class: 'muted small', style: 'flex:0 0 auto', text: label }));
  }

  // 触摸穿透：开启后面板不拦截点击（也无法拖动），通知栏「关闭穿透」或此处可关
  const passCb = h('input', { type: 'checkbox', id: 'float_pass' }) as HTMLInputElement;
  floatPassCb = passCb;
  passCb.onchange = () => {
    void floatCall((c) => n.setFloatingPassThrough!(c, passCb.checked));
  };

  // vivo/iQOO 专项引导（上轮悬浮窗不可用的高发原因：ROM 单项权限矩阵未开）
  const vivoCard = h('div', { class: 'card', style: 'display:none' });
  const appInfoBtn = h('button', { class: 'ghost small', text: '打开应用信息页' });
  appInfoBtn.onclick = () => n.openAppInfo?.();
  vivoCard.append(
    h('div', { class: 'muted small' }, '检测到 vivo/iQOO 机型：以下权限未开时悬浮窗会被系统静默拦截'),
    h('ul', { class: 'muted small', style: 'margin:6px 0 0 18px;padding:0' },
      h('li', { text: '设置 → 应用 → AI-Mobile → 权限 →「显示悬浮窗」允许' }),
      h('li', { text: '同一页面的「后台弹出界面」保持允许' }),
      h('li', { text: 'i管家 → 应用管理 → AI-Mobile：信任 + 后台高耗电允许' })),
    h('div', { class: 'row', style: 'margin-top:8px' }, appInfoBtn),
  );

  box.append(
    h('div', { class: 'row' },
      h('div', { class: 'grow', text: '桌面悬浮窗' }),
      st),
    h('div', { class: 'row', style: 'margin-top:8px' }, toggleBtn, permBtn),
    h('div', { class: 'row', style: 'margin-top:8px' },
      h('span', { class: 'muted small', style: 'flex:0 0 auto', text: '监控设备' }),
      devSel),
    h('div', { class: 'row', style: 'margin-top:8px' }, range, opacityVal),
    zeroHint,
    h('div', { class: 'row', style: 'margin-top:8px' },
      passCb,
      h('label', { for: 'float_pass', class: 'muted small', style: 'flex:0 0 auto', text: '触摸穿透（点按落到下层应用）' })),
    rowsBox,
    h('div', { class: 'muted small', style: 'margin-top:6px' },
      '开启后回到桌面/其他应用也能实时看到电脑状态。面板无背景卡片，文字直接叠在屏幕上（带投影保证可读）；拖动面板松手自动贴左右边。关闭与穿透开关在通知栏。'),
  );

  // 进入设置页：以原生侧真实状态刷新（覆盖 PWA 重装/进程被杀等场景）
  void (async () => {
    const s = await floatCall((cb) => n.getFloatingState!(cb));
    toggleBtn.disabled = false;
    toggleBtn.dataset.on = s.running ? '1' : '';
    toggleBtn.textContent = s.running ? '关闭悬浮窗' : '开启悬浮窗';
    permBtn.style.display = s.canDrawOverlays ? 'none' : '';
    vivoCard.style.display = s.isVivo ? '' : 'none';
    if (!s.running) setFloatStatus('未开启');
    else if (s.ready) setFloatStatus('运行中 · 已连接中继', 'ok');
    else setFloatStatus('运行中 · 连接中…');
    if (typeof s.opacity === 'number') setOpacityUi(s.opacity);
    if (typeof s.passThrough === 'boolean') passCb.checked = s.passThrough;
    // 以原生侧存储回填内容行勾选（覆盖只在 App 内改过 / 未存过 = 全选）
    if (typeof s.rows === 'string' && s.rows.trim()) {
      const nativeRows = s.rows.split(',').map((x) => x.trim()).filter(Boolean);
      for (const [k, cb] of rowChecks) cb.checked = nativeRows.includes(k);
    } else {
      for (const cb of rowChecks.values()) cb.checked = true;
    }
  })();

  return h('div', null, box, vivoCard);
}

function renderConnect(): void {
  clear(app);
  const urlInput = h('input', {
    placeholder: '例如 wss://ai-mobile.example.com 或 ws://192.168.1.5:9000',
    value: state.serverUrl,
  });
  const tokenInput = h('input', {
    placeholder: '用户 token（云端 config 里的 users.*.token）',
    type: 'password',
    value: state.token,
  });
  const btn = h('button', { text: '连接', style: 'width:100%' });
  const statusEl = h('div', { class: 'muted small', style: 'margin-top:12px' });

  btn.onclick = () => {
    let url: string;
    try {
      url = normalizeWsUrl((urlInput as HTMLInputElement).value);
    } catch (e) {
      toast((e as Error).message);
      return;
    }
    const token = (tokenInput as HTMLInputElement).value.trim();
    if (!token) {
      toast('请输入用户 token');
      return;
    }
    state.serverUrl = url;
    state.token = token;
    save(LS.url, url);
    save(LS.token, token);
    state.authError = '';
    btn.disabled = true;
    btn.textContent = '连接中…';
    client.connect(url, token);
  };

  connectStatusEl = statusEl;
  client.onAuthError = (reason) => {
    state.authError = reason;
    btn.disabled = false;
    btn.textContent = '连接';
    statusEl.textContent = '';
    toast(`认证失败：${reason}`);
  };

  app.append(
    h('div', { class: 'card' },
      h('h1', { text: 'AI-Mobile' }),
      h('div', { class: 'muted small' }, '远程管理 Windows 电脑 · 供应商切换 · 系统监控 · 快捷控制 · 文件'),
      h('label', { text: '服务器地址' }),
      urlInput,
      h('label', { text: '用户 Token' }),
      tokenInput,
      h('div', { class: 'spacer' }),
      btn,
      statusEl,
    ),
  );

  // 应用内「检查更新」：原生壳里走本地 Updater（下载/校验/触发系统安装；纯浏览器则提示）
  const upd = updateControls();
  app.append(h('div', { class: 'card', style: 'margin-top:8px' }, upd.btn, upd.clrBtn, upd.status));
}

/* ------------------------------ 视图：主界面 ------------------------------ */

function renderHome(): void {
  clear(app);

  const sel = h('select', { class: 'grow' });
  // 离线机不上列表（状态由顶部面板回显），下拉只列在线设备
  for (const d of onlineDevices()) {
    // 头部下拉空间有限，只放 名称（native select 截断无省略号，带 id 会像缺括号）；完整 id 在设备页
    sel.append(h('option', { value: d.id, text: d.name }));
  }
  sel.value = state.selected;
  sel.onchange = () => {
    void stopFsWatch(); // 旧设备的目录监视也停
    resetFsState();
    stopLive(); // 停旧设备直播（stop 命令发往旧设备）
    if (probeWatching && state.selected) client.cmdNoWait(state.selected, 'probe.unwatch'); // 停旧设备探针
    stopProbeWatch(true); // 清监控/快捷控制缓存（新设备数据未到，先显示「加载中」）
    state.selected = sel.value;
    save(LS.device, sel.value);
    state.windows = [];
    client.subscribe(state.selected);
    refreshProviders();
    refreshStatus();
    syncProbeWatch(); // 当前 tab 需要探针则对新设备重新 watch（离线设备会在 catch 里挂起重试）
  };

  // 顶部设备状态回显：绿点=在线，红点+红字「离线」=选中设备离线（离线机不占下拉条目）
  const selectedOnline = state.devices.some((d) => d.id === state.selected && d.online);
  const curDev = currentDevice();
  // 展开态首行：在线 → 绿点 + 在线设备下拉；离线 → 红点 + 设备名 +「离线」
  const deviceRow = selectedOnline
    ? h('div', { class: 'grow row', style: 'min-width:0' },
        h('span', { class: 'dot ok', style: 'margin:0' }), sel)
    : curDev
      ? h('div', { class: 'grow row', style: 'min-width:0' },
          h('span', { class: 'dot err', style: 'margin:0' }),
          h('span', { class: 'ell', text: curDev.name }),
          h('span', { class: 'err small', style: 'flex:0 0 auto' }, '离线'))
      : h('div', { class: 'grow row', style: 'min-width:0' },
          h('span', { class: 'muted', text: '未选择设备' }));

  // 折叠态头部用：.row gap 已有 8px，.dot 自带 margin 归零避免叠加
  const statusDot = h('span', { class: `dot ${selectedOnline ? 'ok' : 'err'}`, style: 'margin:0' });

  // 应用内「检查更新」入口也放到主界面头部（连上后连接页不可见，避免无处升级）；
  // 展开时首行=设备下拉，副行=供应商·系统·bridge 版本，按钮行另起一行
  const upd = updateControls();

  const disBtn = h('button', { class: 'danger', text: '断开' });
  disBtn.onclick = () => {
    if (!confirm('断开与中继服务器的连接？')) return;
    stopLive();
    if (probeWatching && state.selected) client.cmdNoWait(state.selected, 'probe.unwatch');
    client.disconnect();
    state.connected = false;
    stopProbeWatch(true);
    stopLegacyPolling();
    resetFsState();
    render();
  };

  // 字体大小步进（CSS 变量 --fscale 全局生效，无需重绘）
  const fontMinus = h('button', { class: 'ghost small', text: 'A-', title: '减小字体' });
  fontMinus.onclick = () => stepFont(-1);
  const fontPlus = h('button', { class: 'ghost small', text: 'A+', title: '增大字体' });
  fontPlus.onclick = () => stepFont(1);

  // 折叠开关：折叠后头部只剩一行摘要（状态点 + 设备名 + bridge 版本 + 断开 + 展开箭头）
  const foldBtn = h('button', {
    class: 'ghost small',
    text: state.headerCollapsed ? '▸' : '▾',
    title: state.headerCollapsed ? '展开头部面板' : '折叠头部面板',
  });
  foldBtn.onclick = () => {
    state.headerCollapsed = !state.headerCollapsed;
    save(LS.hdr, state.headerCollapsed ? '1' : '');
    render();
  };

  const header = h('div', { class: 'card hdr', style: 'padding:8px 12px;margin:8px 12px' },
    h('div', { class: 'row' },
      // 展开时首行=状态点+在线设备下拉（离线时替换为红点+「离线」状态行）；折叠时保留名称摘要
      state.headerCollapsed
        ? h('div', { class: 'grow row' },
            statusDot,
            h('span', { class: 'ell', text: curDev?.name ?? '未选择设备' }),
            curDev && !selectedOnline ? h('span', { class: 'err small', style: 'flex:0 0 auto' }, '离线') : null,
            h('span', { class: 'badge', style: 'flex:0 0 auto' }, `bridge ${state.status?.version ?? '…'}`))
        : deviceRow,
      disBtn,
      foldBtn,
    ),
    ...(state.headerCollapsed ? [] : [
      h('div', { class: 'muted small ell', style: 'margin-top:6px' },
        !selectedOnline
          ? h('span', { class: 'err' }, `离线${state.status ? ` · bridge ${state.status.version ?? '…'}` : ''}`)
          : state.status
            ? `${state.status.currentProvider ?? '无供应商'} · ${state.status.os === 'win32' ? 'Windows' : state.status.os} · bridge ${state.status.version ?? '…'}`
            : '…'),
      h('div', { class: 'row', style: 'margin-top:6px' },
        upd.btn,
        upd.clrBtn,
        fontMinus,
        fontPlus,
        h('span', { class: 'grow' }),
      ),
      upd.status,
    ]),
  );

  const tabs = h('div', { class: 'tabs' },
    h('button', { text: '设备' }),
    h('button', { text: '供应商' }),
    h('button', { text: '监控' }),
    h('button', { text: '直播' }),
    h('button', { text: '快捷控制' }),
    h('button', { text: '文件' }),
    h('button', { text: '设置' }),
  );
  const tabBtns = tabs.querySelectorAll('button');
  tabBtns.forEach((b, i) => b.addEventListener('click', () => {
    const next = TAB_KEYS[i];
    if (state.tab === next) return;
    if (state.tab === 'files' && next !== 'files') void stopFsWatch();
    if (state.tab === 'monitor' && next !== 'monitor' && probeDegraded) stopLegacyPolling();
    if (state.tab === 'live' && next !== 'live') stopLive(); // 离开直播即停（省流量）
    state.tab = next;
    syncProbeWatch(); // 离开监控/快捷控制 → unwatch；进入/互切 → watch（幂等）
    render();
  }));
  const activeIdx = TAB_KEYS.indexOf(state.tab);
  tabBtns[activeIdx].classList.add('active');

  const panel = h('div', { class: 'panel-wrap' });
  const updatePanel = () => {
    clear(panel);
    if (state.tab === 'devices') panel.append(renderDevices());
    else if (state.tab === 'providers') panel.append(renderProviders());
    else if (state.tab === 'monitor') panel.append(renderMonitor());
    else if (state.tab === 'live') panel.append(renderLive());
    else if (state.tab === 'screen') panel.append(renderScreen());
    else if (state.tab === 'settings') panel.append(renderSettings());
    else panel.append(renderFiles());
  };
  updatePanel();

  app.append(header, tabs, panel);
}

function renderDevices(): HTMLElement {
  const box = h('div', { class: 'card' });
  if (state.devices.length === 0) {
    box.append(h('div', { class: 'muted' }, '云端未配置可控制的设备'));
  }
  // 离线机不上列表（状态由顶部面板回显），这里只列在线设备
  const online = onlineDevices();
  if (state.devices.length > 0 && online.length === 0) {
    box.append(h('div', { class: 'muted' }, '所有设备离线，等待设备上线…'));
  }
  for (const d of online) {
    const isCur = d.id === state.selected;
    const row = h('div', { class: 'item row' },
      onlineDot(d.online),
      h('div', { class: 'grow' },
        h('div', { class: 'ell' }, d.name, isCur ? h('span', { class: 'badge' }, '✓ 当前') : null),
        h('div', { class: 'muted small ell' }, d.id)),
      // 当前设备不再提供「选择」（语义矛盾），显示已连接
      isCur ? h('span', { class: 'muted small', text: '已连接' })
        : h('button', { class: 'ghost small', text: '选择' }),
    );
    const btn = row.querySelector<HTMLButtonElement>('button');
    if (btn) {
      btn.onclick = () => {
        void stopFsWatch();
        resetFsState();
        if (probeWatching && state.selected) client.cmdNoWait(state.selected, 'probe.unwatch');
        stopProbeWatch(true);
        state.selected = d.id;
        save(LS.device, d.id);
        state.windows = [];
        client.subscribe(d.id);
        refreshProviders();
        refreshStatus();
        syncProbeWatch();
        render();
      };
    }
    box.append(row);
  }
  if (online.length > 0 && online.length < state.devices.length) {
    box.append(h('div', { class: 'muted small', style: 'margin-top:4px' },
      `另有 ${state.devices.length - online.length} 台设备离线`));
  }
  box.append(h('div', { class: 'spacer' }));
  const info = h('div', { class: 'muted small ell' });
  refreshStatus().then(() => {
    if (state.status) {
      info.textContent = `bridge v${state.status.version} · ${state.status.os} · 工作目录 ${state.status.workspaceDir}`;
    }
  });
  box.append(info);
  return box;
}

function renderProviders(): HTMLElement {
  const box = h('div', { class: 'card' });
  const body = h('div', {});
  box.append(body);

  const rebuild = () => {
    clear(body);
    body.append(h('div', { class: 'muted small', style: 'margin-bottom:4px' },
      '每个目标各有自己的供应商目录：cc-switch 目录只读（请在 cc-switch 里改），happy 等目录可在本页新增/编辑/删除，两者互不影响。'));
    if (state.targets.length === 0) {
      body.append(h('div', { class: 'muted' }, '没有可切换的目标'));
      return;
    }

    // 顶部：当前启用（每个目标一目了然）—— badge 固定列 + 单行省略的名称列，避免换行错位
    body.append(h('div', { class: 'sec' }, '当前启用'));
    const curBox = h('div', {
      class: 'item cur-grid',
      style: 'padding:8px 12px;border-radius:8px;background:var(--panel-2);margin-bottom:4px',
    });
    for (const tt of state.targets) {
      const pr = state.providers.find((p) => p.id === tt.currentProviderId);
      curBox.append(
        h('span', { class: 'badge', text: tt.name }),
        pr
          ? h('span', { class: 'cur-name', text: pr.name + (tt.currentModel ? ' · ' + tt.currentModel : '') })
          : h('span', { class: 'cur-name muted small', text: '未设置' }),
      );
    }
    body.append(curBox);

    // 默认选中的目标 → 其当前供应商 / 模型
    if (!cfgTarget || !state.targets.some((x) => x.id === cfgTarget)) cfgTarget = state.targets[0].id;
    const t = state.targets.find((x) => x.id === cfgTarget) ?? state.targets[0];
    // 只看该目标自己的供应商（老 bridge 无 targetId 字段时全部显示，向前兼容）
    const myProviders = state.providers.filter((p) => !p.targetId || p.targetId === t.id);
    const curP = myProviders.find((p) => p.id === t.currentProviderId) ?? myProviders[0] ?? null;
    if (!cfgProvider || !myProviders.some((p) => p.id === cfgProvider)) {
      cfgProvider = curP?.id ?? '';
      cfgModel = t.currentModel ?? curP?.model ?? '';
    }

    // ① 配置哪个目标
    const targetSel = h('select', {});
    targetSel.append(h('option', { value: '', text: '— 选择 —' }));
    for (const tt of state.targets) targetSel.append(h('option', { value: tt.id, text: tt.name }));
    targetSel.value = cfgTarget;
    targetSel.onchange = () => {
      cfgTarget = (targetSel as HTMLSelectElement).value;
      if (!cfgTarget) return;
      const tt = state.targets.find((x) => x.id === cfgTarget) ?? state.targets[0];
      const nc = state.providers.filter((p) => !p.targetId || p.targetId === tt.id)
        .find((p) => p.id === tt.currentProviderId)
        ?? state.providers.filter((p) => !p.targetId || p.targetId === tt.id)[0]
        ?? null;
      cfgProvider = nc?.id ?? '';
      cfgModel = tt.currentModel ?? nc?.model ?? '';
      provForm = null;
      rebuild();
    };

    // ② 供应商（仅本目标的）
    const provSel = h('select', {});
    if (myProviders.length === 0) {
      provSel.append(h('option', { value: '', text: '（无供应商）' }));
      provSel.disabled = true;
    } else {
      for (const p of myProviders) {
        provSel.append(h('option', { value: p.id, text: `${p.name}${p.id === t.currentProviderId ? '（当前）' : ''}` }));
      }
      provSel.value = cfgProvider;
    }
    provSel.onchange = () => {
      cfgProvider = (provSel as HTMLSelectElement).value;
      cfgModel = ''; // 待真实模型列表加载后自动选中
      provForm = null;
      rebuild();
    };

    // ③ 模型：真实列表来自供应商 API（provider.models → {baseUrl}/v1/models），
    //    选中供应商后异步拉取；失败时回落到 env 提取的静态列表
    const pp = myProviders.find((x) => x.id === cfgProvider);
    const modelSel = h('select', {});
    const modelHint = h('div', { class: 'muted small', style: 'margin-top:4px' });

    const fillModels = (list: string[], hint = '') => {
      clear(modelSel);
      modelHint.textContent = hint;
      // 当前已选模型不在列表里（自定义模型）时保留在首位，避免 UI 与实际应用值脱节
      const finalList = cfgModel && !list.includes(cfgModel) ? [cfgModel, ...list] : list;
      if (finalList.length === 0) {
        modelSel.append(h('option', { value: '', text: '（默认 / 官方）' }));
      } else {
        for (const mm of finalList) modelSel.append(h('option', { value: mm, text: mm }));
      }
      modelSel.value = cfgModel && finalList.includes(cfgModel) ? cfgModel : (finalList[0] ?? '');
      cfgModel = (modelSel as HTMLSelectElement).value;
    };

    const loadRealModels = async () => {
      if (!cfgProvider) return;
      const myProvider = cfgProvider;
      if (providerModelsCache[myProvider]) {
        fillModels(providerModelsCache[myProvider]);
        return;
      }
      clear(modelSel);
      modelSel.append(h('option', { value: cfgModel, text: '（获取模型列表…）' }));
      try {
        const o = await cmd('provider.models', { providerId: myProvider }, 30_000);
        const data = o.result.data as ProviderModelsResponse | undefined;
        const list = data?.models ?? [];
        if (!list.length) throw new Error('供应商未返回模型');
        providerModelsCache[myProvider] = list;
        // 期间用户已切换供应商（rebuild 换了 DOM）则丢弃本次结果
        if (cfgProvider === myProvider && modelSel.isConnected) fillModels(list);
      } catch {
        if (cfgProvider === myProvider && modelSel.isConnected) {
          fillModels(pp?.models ?? [], '实时模型列表获取失败，已显示本地配置的模型');
        }
      }
    };
    modelSel.onchange = () => {
      cfgModel = (modelSel as HTMLSelectElement).value;
    };

    // 应用按钮：全宽，作为表单底部锚点
    const apply = h('button', { text: `应用切换并写入 ${t.name}`, style: 'width:100%' });
    if (!cfgProvider) apply.disabled = true;
    apply.onclick = async () => {
      if (!cfgProvider) {
        toast('请选择一个供应商');
        return;
      }
      apply.disabled = true;
      try {
        const payload: Record<string, unknown> = { target: cfgTarget, providerId: cfgProvider };
        if (cfgModel) payload.model = cfgModel;
        await cmd('provider.switch', payload);
        const name = state.providers.find((x) => x.id === cfgProvider)?.name ?? cfgProvider;
        toast(`已切换 ${t.name} → ${name}${cfgModel ? ' · ' + cfgModel : ''}`, 'ok');
        await refreshProviders();
        // 重置为新的当前状态
        if (state.targets.some((x) => x.id === cfgTarget)) {
          cfgProvider = '';
          cfgModel = '';
        }
        rebuild();
      } catch (e) {
        toast((e as Error).message);
      } finally {
        apply.disabled = false;
      }
    };

    // ④ 目录维护：可编辑目标才显示新增/编辑/删除（cc-switch 目录只读）
    const emptyHint = myProviders.length === 0
      ? h('div', { class: 'muted small', style: 'margin-top:6px' },
        t.editable
          ? `目标「${t.name}」还没有供应商，点下方「＋ 新增供应商」创建`
          : '请先在 cc-switch 里添加')
      : null;

    let crud: HTMLElement | null = null;
    if (t.editable) {
      const row = h('div', { class: 'row', style: 'margin-top:10px;flex-wrap:wrap' });
      const addBtn = h('button', { class: 'ghost small', text: '＋ 新增供应商' });
      addBtn.onclick = () => { provForm = { id: null }; rebuild(); };
      row.append(addBtn);
      if (curP) {
        const editBtn = h('button', { class: 'ghost small', text: '编辑' });
        editBtn.onclick = () => { provForm = { id: curP.id }; rebuild(); };
        row.append(editBtn);

        const delBtn = h('button', { class: 'danger small', text: '删除' });
        delBtn.onclick = () => {
          if (!confirm(`从「${t.name}」的供应商目录中删除「${curP.name}」？\n\n只从目录移除，已写入 settings.json 的配置不会回滚（该供应商当前仍在生效）。`)) return;
          delBtn.disabled = true;
          cmd('provider.delete', { target: t.id, providerId: curP.id }, 30_000)
            .then(async () => {
              provForm = null;
              cfgProvider = '';
              await refreshProviders();
              toast('已从目录删除', 'ok');
              rebuild();
            })
            .catch((e) => {
              delBtn.disabled = false;
              toast((e as Error).message);
            });
        };
        row.append(delBtn);
      }
      crud = h('div', {}, row);
      // 内联表单（不用 modal：追加在卡片里，滚动就是它自己的上下文）
      if (provForm) {
        const editId = provForm.id;
        const isNew = editId === null;
        const ep = isNew ? null : (myProviders.find((x) => x.id === editId) ?? null);

        const nameEl = input(ep?.name ?? '', '例如：火山Agentplan');
        const urlEl = input(ep?.baseUrl ?? '', 'https://api.example.com');
        const keyEl = input(
          '',
          ep && ep.apiKeyMasked && ep.apiKeyMasked !== '—'
            ? `已保存 ${ep.apiKeyMasked}，留空＝不修改`
            : 'sk-... / ark-...',
          'text',
          { password: true },
        );
        const clearKeyEl = h('input', { type: 'checkbox' });
        const modelEl = input(ep?.model ?? '', 'ark-code-latest[1M]');
        const modelsEl = input(ep?.models.join('\n') ?? '', '每行一个模型', 'text', { multiline: true });
        const extraEl = input('', '{ "CLAUDE_CODE_MAX_CONTEXT_TOKENS": "1000000" }', 'text', { multiline: true });

        const form = h('div', { class: 'item', style: 'padding:10px 0' });
        form.append(
          h('label', { text: '名称' }), nameEl,
          h('label', { text: 'Base URL（ANTHROPIC_BASE_URL）' }), urlEl,
          h('label', { text: 'API Key（ANTHROPIC_AUTH_TOKEN，留空＝不修改）' }), keyEl,
          h('label', { class: 'row', style: 'margin:8px 0 0;font-weight:400' }, clearKeyEl,
            h('span', { class: 'small', text: '清除已保存的 Key（切换后该供应商将无 key）' })),
          h('label', { text: '主模型（ANTHROPIC_MODEL）' }), modelEl,
          h('label', { text: '模型列表（每行一个；改动后依次写入 OPUS/SONNET/HAIKU/FABLE + SUBAGENT）' }), modelsEl,
          h('label', { text: '额外 env JSON（可选，覆盖上面的字段）' }), extraEl,
        );

        const saveBtn = h('button', { text: isNew ? '新增并保存' : '保存修改', style: 'width:100%;margin-top:12px' });
        const cancelBtn = h('button', { class: 'ghost small', text: '取消', style: 'width:100%;margin-top:8px' });
        cancelBtn.onclick = () => { provForm = null; rebuild(); };
        saveBtn.onclick = async () => {
          const name = String(nameEl.value).trim();
          if (!name) { toast('请填写供应商名称'); return; }
          if (name.length > 60) { toast('名称最多 60 字'); return; }

          // env 补丁：有值→覆盖、null→删除、不发送→保持不变（避免把打码 key 回填成真 key）
          const env: Record<string, string | null> = {};
          const url = String(urlEl.value).trim();
          if (url) env.ANTHROPIC_BASE_URL = url;
          const key = String(keyEl.value).trim();
          if (key) {
            if (key.includes('…')) { toast('请重新输入完整 key'); return; }
            env.ANTHROPIC_AUTH_TOKEN = key;
          } else if (clearKeyEl.checked) {
            env.ANTHROPIC_AUTH_TOKEN = null;
            env.ANTHROPIC_API_KEY = null;
          }
          const model = String(modelEl.value).trim();
          if (model) env.ANTHROPIC_MODEL = model;
          const models = String(modelsEl.value).split('\n').map((s) => s.trim()).filter(Boolean);
          if (models.join('\n') !== (ep?.models ?? []).join('\n')) {
            const keys = [
              'ANTHROPIC_DEFAULT_OPUS_MODEL',
              'ANTHROPIC_DEFAULT_SONNET_MODEL',
              'ANTHROPIC_DEFAULT_HAIKU_MODEL',
              'ANTHROPIC_DEFAULT_FABLE_MODEL',
              'CLAUDE_CODE_SUBAGENT_MODEL',
            ];
            keys.forEach((k, i) => { env[k] = models[i] ?? null; });
          }
          const extraRaw = String(extraEl.value).trim();
          if (extraRaw) {
            let obj: unknown;
            try {
              obj = JSON.parse(extraRaw);
            } catch {
              toast('额外 env 不是合法 JSON');
              return;
            }
            if (!obj || typeof obj !== 'object' || Array.isArray(obj)) { toast('额外 env 必须是 JSON 对象'); return; }
            for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
              if (v === null) env[k] = null;
              else if (typeof v === 'string') env[k] = v;
              else { toast(`额外 env 的 ${k} 必须是字符串或 null`); return; }
            }
          }

          saveBtn.disabled = true;
          try {
            await cmd('provider.save', {
              target: t.id,
              id: isNew ? undefined : editId,
              name,
              env,
            }, 30_000);
            provForm = null;
            cfgProvider = '';
            await refreshProviders();
            toast('已保存（点「应用切换」后才写入 settings.json）', 'ok');
            rebuild();
          } catch (e) {
            toast((e as Error).message);
          } finally {
            saveBtn.disabled = false;
          }
        };
        form.append(saveBtn, cancelBtn);
        crud.append(form);
      }
    }

    const parts: Array<Node | null> = [
      h('label', { text: '配置哪个' }),
      targetSel,
      h('label', { text: '供应商' }),
      provSel,
      emptyHint,
      h('label', { text: '模型' }),
      modelSel,
      modelHint,
      crud,
      h('div', { class: 'spacer' }),
      apply,
    ];
    body.append(...parts.filter((x): x is Node => x !== null));

    // 拉取当前选中供应商的真实模型列表（有缓存则同步填充）
    void loadRealModels();
  };

  rebuild();
  return box;
}

/* ------------------------------ 入口 ------------------------------ */

client.onFsEvent = (e) => {
  if (state.tab !== 'files' || !state.selected) return; // 离开 tab 时 watch 已停，防御性忽略
  const viewing = state.fsViewing;
  // 正在查看的文件出现在变更列表里 → 静默重读
  if (viewing) {
    const base = fsBasename(viewing.path).toLowerCase();
    if (e.files.some((f) => f.toLowerCase() === base)) void fsRefreshFile();
  }
  // 变更的是当前目录 → 重列（文件保存同时改 mtime，与上面重读并存）
  if (e.path.toLowerCase() === state.fsPath.toLowerCase()) void fsRefreshList();
};
client.onProbeData = applyProbe;
client.onScreenFrame = (f) => {
  if (!liveActive) return; // 已停止/切走后仍在途的帧
  liveLastFrameTs = Date.now();
  if (state.tab !== 'live' || !liveImgEl) return;
  liveImgEl.src = 'data:image/jpeg;base64,' + f.jpg;
  if (liveHintEl) liveHintEl.style.display = 'none';
};
client.onConnState = (s) => {
  if (connectStatusEl) {
    if (s === 'connecting') connectStatusEl.textContent = '正在连接中继服务器…';
    else if (s === 'open') connectStatusEl.textContent = '';
    else if (s === 'closed' && !state.connected) connectStatusEl.textContent = '连接失败或已断开，自动重连中…';
  }
  // 直播状态行跟随连接态（liveActive 保持，重连后自动续播）
  if (s !== 'open') liveStatusRefresh();
  // 已连上后断线：把设备标记为离线，让界面立刻反映状态
  if (s === 'closed' && state.connected) {
    stopProbeWatch(false); // watch 定时器随断连失效；不清数据，重连后首帧覆盖
    stopLegacyPolling();
    state.devices = state.devices.map((d) => ({ ...d, online: false }));
    render();
  }
};
client.onStatus = (devices) => {
  state.devices = devices;
  if (!state.devices.some((d) => d.id === state.selected)) {
    state.selected = state.devices[0]?.id ?? '';
    save(LS.device, state.selected);
  }
  render();
};
client.onAuthOk = (devices) => {
  state.connected = true;
  state.devices = devices;
  if (!state.devices.some((d) => d.id === state.selected)) {
    state.selected = state.devices[0]?.id ?? '';
    save(LS.device, state.selected);
  }
  if (state.selected) client.subscribe(state.selected);
  syncProbeWatch(); // 重连后恢复 watch（bridge 侧断连时已停采）
  // 直播：重连后恢复（bridge 侧截取未断则续收帧；断了由 screen.start 重新拉起）。
  // 未在看直播时补发一次 screen.stop —— 页面刷新/重连后本地状态丢失，
  // 桥接不会自行感知手机端是否还在看，防孤儿截取流。
  if (liveActive && state.tab === 'live') void startLive();
  else if (liveActive) stopLive(); // 防御：理论上切 tab 已停
  else if (state.selected) client.cmdNoWait(state.selected, 'screen.stop');
  refreshProviders();
  refreshStatus();
  render();
  // 凭据/设备变化后：若悬浮窗处于开启态，自动同步新配置（幂等；
  // Service 端 url/token 变化重建连接，仅换设备则重订阅）
  const fn = native();
  if (load(LS.floatEnabled) === '1' && fn?.setFloatingConfig) {
    void floatCall((cb) => fn.setFloatingConfig!(cb, floatPayload(true)));
  }
};
client.onError = (reason) => toast(reason);

function render(): void {
  // 连接页靠页面滚动；主界面固定一屏、内部滚动
  app.classList.toggle('home', state.connected);
  if (!state.connected) renderConnect();
  else renderHome();
}

setupPullToRefresh();
render();
